import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Dna,
  RefreshCw,
  Plus,
  Search,
  Filter,
  Eye,
  Edit,
  Trash2,
  ChevronDown,
  X,
  AlertCircle,
  CheckCircle,
  Clock,
  Printer,
  Users,
  BookOpen,
  Zap,
  BarChart3,
  ChevronUp,
  Upload,
  FileText,
  FolderOpen,
  HardDrive,
  Network,
  File,
  Files,
  RotateCw,
  MessageSquare,
} from 'lucide-react';
import PrintButton from '../components/PrintButton';
import ResearchTab from './crm/ResearchTab';
import ProprietaryVaultView from './crm/ProprietaryVaultView';
import ResearchStudiesPage from './ResearchStudiesPage';
import ResearchDatabasePage from './ResearchDatabasePage';
import R6BiogeoTab from './biolab/R6BiogeoTab';
import HypothesesTab from './biolab/HypothesesTab';

const COLORS = {
  primary: '#2E8B98',
  teal: '#2E8B98',
  green: '#62BB46',
  orange: '#F26522',
  red: '#DC3545',
  dark: '#256F7A',
  light: '#F8F9FA',
  white: '#FFFFFF',
  border: '#e2e8f0',
  text: '#2d3748',
  textLight: '#718096',
};

// ── Research Repository navigation — 16 tabs consolidated into grouped
// dropdown menus (Jun 12 2026, Daniel). Replaces the single overflow-scroll
// tab row. Each menu groups related functions; the menu highlights when it
// holds the active tab. Tab keys + render logic are unchanged.
const TAB_LABELS = {
  research_workspace: 'Research Workspace ✨',
  hypotheses: 'Hypotheses',
  r6_biogeo: 'R6 Bio-Geometric 🌀',
  vault: 'Proprietary Vault 🔒',
  evidence: 'Evidence & Protocols',
  protocols: 'Protocols',
  published: 'Published Research',
  studies: 'Studies & Cohorts',
  treatments: 'Patient Treatments',
  comparisons: 'Comparative Analysis',
  analysis: 'AI Analysis',
  intelligence: 'Intelligence Dashboard',
  documents: 'Documents',
  ingestion: 'Data Ingestion',
  doctor: 'Doctor Dashboard',
  compliance: 'Compliance',
};
const TAB_GROUPS = [
  { label: 'Workspace', tabs: ['research_workspace', 'r6_biogeo', 'hypotheses'] },
  { label: 'Protocols & Evidence', tabs: ['vault', 'evidence', 'protocols', 'published'] },
  { label: 'Studies & Patients', tabs: ['studies', 'treatments', 'comparisons'] },
  { label: 'AI & Intelligence', tabs: ['analysis', 'intelligence'] },
  { label: 'Data', tabs: ['documents', 'ingestion'] },
  { label: 'Oversight', tabs: ['doctor', 'compliance'] },
];

function BiolabNavMenu({ group, activeTab, onSelect }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    function onDoc(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false); }
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);
  const holdsActive = group.tabs.includes(activeTab);
  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        onClick={() => setOpen((o) => !o)}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 6, padding: '10px 14px',
          border: 'none', background: holdsActive ? '#e7f3f5' : 'none', borderRadius: 6,
          color: holdsActive ? COLORS.primary : COLORS.textLight,
          fontSize: 14, fontWeight: holdsActive ? 600 : 500, cursor: 'pointer', whiteSpace: 'nowrap',
        }}
      >
        {group.label}
        <ChevronDown size={14} style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform .15s' }} />
      </button>
      {open && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, marginTop: 2, zIndex: 50, minWidth: 210,
          background: COLORS.white, border: `1px solid ${COLORS.border}`, borderRadius: 8,
          boxShadow: '0 8px 24px rgba(0,0,0,0.12)', overflow: 'hidden', padding: '4px 0',
        }}>
          {group.tabs.map((t) => (
            <button
              key={t}
              onClick={() => { onSelect(t); setOpen(false); }}
              style={{
                display: 'block', width: '100%', textAlign: 'left', padding: '9px 14px', border: 'none',
                background: activeTab === t ? '#f0f7f8' : 'transparent', cursor: 'pointer',
                color: activeTab === t ? COLORS.primary : COLORS.text, fontSize: 13.5,
                fontWeight: activeTab === t ? 600 : 400,
              }}
              onMouseEnter={(e) => { if (activeTab !== t) e.currentTarget.style.background = COLORS.light; }}
              onMouseLeave={(e) => { if (activeTab !== t) e.currentTarget.style.background = 'transparent'; }}
            >
              {TAB_LABELS[t] || t}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

const CELL_TYPE_OPTIONS = [
  'Autologous',
  'Allogeneic',
  'iPSC',
  'ESC',
  'MSC',
  'HSC',
  'CAR-T',
  'Other',
];

const REGULATORY_STATUS_OPTIONS = [
  'Research Only',
  'IND Filed',
  'FDA Approved',
  'EMA Approved',
  'CE Marked',
  'Investigational',
];

const OUTCOME_OPTIONS = [
  'Pending',
  'Improved',
  'Stable',
  'Worsened',
  'Adverse Event',
  'Complete Response',
];

const STUDY_PHASE_OPTIONS = [
  'Preclinical',
  'Phase I',
  'Phase II',
  'Phase III',
  'Phase IV',
  'Case Report',
  'Systematic Review',
  'Meta-Analysis',
];

const AI_ANALYSIS_TYPES = [
  'protocol_comparison',
  'outcome_prediction',
  'optimization_suggestion',
  'safety_review',
  'literature_synthesis',
  'treatment_recommendation',
  'trend_analysis',
  'risk_assessment',
];

const CATEGORY_BADGE_COLORS = {
  Autologous: { bg: '#dcfce7', text: '#15803d' },
  Allogeneic: { bg: '#dbeafe', text: '#1d4ed8' },
  iPSC: { bg: '#f5f3ff', text: '#6d28d9' },
  ESC: { bg: '#fef3c7', text: '#b45309' },
  MSC: { bg: '#e0f2fe', text: '#0369a1' },
  HSC: { bg: '#fef2f2', text: '#DC3545' },
  'CAR-T': { bg: '#ffe4e6', text: '#be123c' },
  Other: { bg: '#f1f5f9', text: '#475569' },
};

const OUTCOME_COLORS = {
  Pending: { bg: '#f1f5f9', text: '#475569' },
  Improved: { bg: '#dcfce7', text: '#15803d' },
  Stable: { bg: '#dbeafe', text: '#1d4ed8' },
  Worsened: { bg: '#fef3c7', text: '#b45309' },
  'Adverse Event': { bg: '#fef2f2', text: '#DC3545' },
  'Complete Response': { bg: '#f0fdf4', text: '#166534' },
};

// KPI Card Component
function KPICard({ label, value, icon: Icon, color }) {
  return (
    <div
      style={{
        background: '#F8F9FA',
        boxShadow: '5px 5px 10px rgba(186,199,216,0.45), -5px -5px 10px rgba(255,255,255,0.85)',
        borderRadius: 12,
        padding: 20,
        textAlign: 'center',
      }}
    >
      <div style={{ marginBottom: 12, color }}>
        <Icon size={24} />
      </div>
      <div style={{ fontSize: 28, fontWeight: 700, color: COLORS.dark, marginBottom: 4 }}>
        {value}
      </div>
      <div style={{ fontSize: 13, color: COLORS.textLight }}>{label}</div>
    </div>
  );
}

// Badge Component
function Badge({ label, bgColor, textColor }) {
  return (
    <span
      style={{
        background: bgColor,
        color: textColor,
        padding: '4px 12px',
        borderRadius: 6,
        fontSize: 12,
        fontWeight: 600,
        display: 'inline-block',
      }}
    >
      {label}
    </span>
  );
}

// Modal Base Component
function Modal({ isOpen, onClose, title, children, isLoading }) {
  if (!isOpen) return null;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
    >
      <div
        style={{
          background: '#F8F9FA',
          borderRadius: 12,
          padding: 32,
          maxWidth: 700,
          width: '90%',
          maxHeight: '90vh',
          overflowY: 'auto',
          boxShadow: '0 20px 25px rgba(0,0,0,0.15)',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: COLORS.dark }}>
            {title}
          </h2>
          <button
            onClick={onClose}
            disabled={isLoading}
            style={{
              background: 'none',
              border: 'none',
              cursor: isLoading ? 'not-allowed' : 'pointer',
              padding: 4,
              color: COLORS.textLight,
              opacity: isLoading ? 0.5 : 1,
            }}
          >
            <X size={20} />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

// New Protocol Modal
function NewProtocolModal({ isOpen, onClose, onSave, isLoading }) {
  const [formData, setFormData] = useState({
    protocolName: '',
    protocolNumber: '',
    description: '',
    cellType: 'MSC',
    category: 'Autologous',
    sourceTissue: '',
    isolationMethod: '',
    cultureMedium: '',
    cultureConditions: '',
    passageRange: '',
    viabilityThreshold: '',
    differentiationMarkers: '',
    growthFactors: '',
    safetyProfile: '',
    regulatoryStatus: 'Research Only',
  });

  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.protocolName || !formData.protocolNumber) {
      setErrors({
        protocolName: !formData.protocolName ? 'Protocol name is required' : '',
        protocolNumber: !formData.protocolNumber ? 'Protocol number is required' : '',
      });
      return;
    }
    onSave(formData);
    setFormData({
      protocolName: '',
      protocolNumber: '',
      description: '',
      cellType: 'MSC',
      category: 'Autologous',
      sourceTissue: '',
      isolationMethod: '',
      cultureMedium: '',
      cultureConditions: '',
      passageRange: '',
      viabilityThreshold: '',
      differentiationMarkers: '',
      growthFactors: '',
      safetyProfile: '',
      regulatoryStatus: 'Research Only',
    });
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Create New Protocol" isLoading={isLoading}>
      <form onSubmit={handleSubmit}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: COLORS.dark, marginBottom: 6 }}>
              Protocol Name *
            </label>
            <input
              type="text"
              name="protocolName"
              value={formData.protocolName}
              onChange={handleChange}
              style={{
                width: '100%',
                padding: 10,
                border: `1px solid ${errors.protocolName ? COLORS.red : COLORS.border}`,
                borderRadius: 6,
                fontSize: 13,
                fontFamily: 'inherit',
              }}
            />
            {errors.protocolName && <div style={{ color: COLORS.red, fontSize: 11, marginTop: 4 }}>{errors.protocolName}</div>}
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: COLORS.dark, marginBottom: 6 }}>
              Protocol Number *
            </label>
            <input
              type="text"
              name="protocolNumber"
              value={formData.protocolNumber}
              onChange={handleChange}
              style={{
                width: '100%',
                padding: 10,
                border: `1px solid ${errors.protocolNumber ? COLORS.red : COLORS.border}`,
                borderRadius: 6,
                fontSize: 13,
                fontFamily: 'inherit',
              }}
            />
            {errors.protocolNumber && <div style={{ color: COLORS.red, fontSize: 11, marginTop: 4 }}>{errors.protocolNumber}</div>}
          </div>
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: COLORS.dark, marginBottom: 6 }}>
            Description
          </label>
          <textarea
            name="description"
            value={formData.description}
            onChange={handleChange}
            rows={3}
            style={{
              width: '100%',
              padding: 10,
              boxShadow: 'inset 2px 2px 4px rgba(186,199,216,0.45), inset -2px -2px 4px rgba(255,255,255,0.85)',
              borderRadius: 6,
              fontSize: 13,
              fontFamily: 'inherit',
              resize: 'vertical',
            }}
          />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: COLORS.dark, marginBottom: 6 }}>
              Cell Type
            </label>
            <select
              name="cellType"
              value={formData.cellType}
              onChange={handleChange}
              style={{
                width: '100%',
                padding: 10,
                boxShadow: 'inset 2px 2px 4px rgba(186,199,216,0.45), inset -2px -2px 4px rgba(255,255,255,0.85)',
                borderRadius: 6,
                fontSize: 13,
                fontFamily: 'inherit',
              }}
            >
              {CELL_TYPE_OPTIONS.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: COLORS.dark, marginBottom: 6 }}>
              Category
            </label>
            <select
              name="category"
              value={formData.category}
              onChange={handleChange}
              style={{
                width: '100%',
                padding: 10,
                boxShadow: 'inset 2px 2px 4px rgba(186,199,216,0.45), inset -2px -2px 4px rgba(255,255,255,0.85)',
                borderRadius: 6,
                fontSize: 13,
                fontFamily: 'inherit',
              }}
            >
              {Object.keys(CATEGORY_BADGE_COLORS).map((cat) => (
                <option key={cat} value={cat}>
                  {cat}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: COLORS.dark, marginBottom: 6 }}>
              Source Tissue
            </label>
            <input
              type="text"
              name="sourceTissue"
              value={formData.sourceTissue}
              onChange={handleChange}
              style={{
                width: '100%',
                padding: 10,
                boxShadow: 'inset 2px 2px 4px rgba(186,199,216,0.45), inset -2px -2px 4px rgba(255,255,255,0.85)',
                borderRadius: 6,
                fontSize: 13,
                fontFamily: 'inherit',
              }}
            />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: COLORS.dark, marginBottom: 6 }}>
              Isolation Method
            </label>
            <input
              type="text"
              name="isolationMethod"
              value={formData.isolationMethod}
              onChange={handleChange}
              style={{
                width: '100%',
                padding: 10,
                boxShadow: 'inset 2px 2px 4px rgba(186,199,216,0.45), inset -2px -2px 4px rgba(255,255,255,0.85)',
                borderRadius: 6,
                fontSize: 13,
                fontFamily: 'inherit',
              }}
            />
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: COLORS.dark, marginBottom: 6 }}>
              Culture Medium
            </label>
            <input
              type="text"
              name="cultureMedium"
              value={formData.cultureMedium}
              onChange={handleChange}
              style={{
                width: '100%',
                padding: 10,
                boxShadow: 'inset 2px 2px 4px rgba(186,199,216,0.45), inset -2px -2px 4px rgba(255,255,255,0.85)',
                borderRadius: 6,
                fontSize: 13,
                fontFamily: 'inherit',
              }}
            />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: COLORS.dark, marginBottom: 6 }}>
              Passage Range
            </label>
            <input
              type="text"
              name="passageRange"
              value={formData.passageRange}
              onChange={handleChange}
              style={{
                width: '100%',
                padding: 10,
                boxShadow: 'inset 2px 2px 4px rgba(186,199,216,0.45), inset -2px -2px 4px rgba(255,255,255,0.85)',
                borderRadius: 6,
                fontSize: 13,
                fontFamily: 'inherit',
              }}
            />
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: COLORS.dark, marginBottom: 6 }}>
              Viability Threshold %
            </label>
            <input
              type="text"
              name="viabilityThreshold"
              value={formData.viabilityThreshold}
              onChange={handleChange}
              style={{
                width: '100%',
                padding: 10,
                boxShadow: 'inset 2px 2px 4px rgba(186,199,216,0.45), inset -2px -2px 4px rgba(255,255,255,0.85)',
                borderRadius: 6,
                fontSize: 13,
                fontFamily: 'inherit',
              }}
            />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: COLORS.dark, marginBottom: 6 }}>
              Regulatory Status
            </label>
            <select
              name="regulatoryStatus"
              value={formData.regulatoryStatus}
              onChange={handleChange}
              style={{
                width: '100%',
                padding: 10,
                boxShadow: 'inset 2px 2px 4px rgba(186,199,216,0.45), inset -2px -2px 4px rgba(255,255,255,0.85)',
                borderRadius: 6,
                fontSize: 13,
                fontFamily: 'inherit',
              }}
            >
              {REGULATORY_STATUS_OPTIONS.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 24 }}>
          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: COLORS.dark, marginBottom: 6 }}>
              Differentiation Markers
            </label>
            <input
              type="text"
              name="differentiationMarkers"
              value={formData.differentiationMarkers}
              onChange={handleChange}
              style={{
                width: '100%',
                padding: 10,
                boxShadow: 'inset 2px 2px 4px rgba(186,199,216,0.45), inset -2px -2px 4px rgba(255,255,255,0.85)',
                borderRadius: 6,
                fontSize: 13,
                fontFamily: 'inherit',
              }}
            />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: COLORS.dark, marginBottom: 6 }}>
              Growth Factors
            </label>
            <input
              type="text"
              name="growthFactors"
              value={formData.growthFactors}
              onChange={handleChange}
              style={{
                width: '100%',
                padding: 10,
                boxShadow: 'inset 2px 2px 4px rgba(186,199,216,0.45), inset -2px -2px 4px rgba(255,255,255,0.85)',
                borderRadius: 6,
                fontSize: 13,
                fontFamily: 'inherit',
              }}
            />
          </div>
        </div>

        <div style={{ marginBottom: 24 }}>
          <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: COLORS.dark, marginBottom: 6 }}>
            Culture Conditions
          </label>
          <textarea
            name="cultureConditions"
            value={formData.cultureConditions}
            onChange={handleChange}
            rows={3}
            style={{
              width: '100%',
              padding: 10,
              boxShadow: 'inset 2px 2px 4px rgba(186,199,216,0.45), inset -2px -2px 4px rgba(255,255,255,0.85)',
              borderRadius: 6,
              fontSize: 13,
              fontFamily: 'inherit',
              resize: 'vertical',
            }}
          />
        </div>

        <div style={{ marginBottom: 24 }}>
          <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: COLORS.dark, marginBottom: 6 }}>
            Safety Profile
          </label>
          <textarea
            name="safetyProfile"
            value={formData.safetyProfile}
            onChange={handleChange}
            rows={3}
            style={{
              width: '100%',
              padding: 10,
              boxShadow: 'inset 2px 2px 4px rgba(186,199,216,0.45), inset -2px -2px 4px rgba(255,255,255,0.85)',
              borderRadius: 6,
              fontSize: 13,
              fontFamily: 'inherit',
              resize: 'vertical',
            }}
          />
        </div>

        <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
          <button
            onClick={onClose}
            disabled={isLoading}
            style={{
              padding: '10px 20px',
              boxShadow: '5px 5px 10px rgba(186,199,216,0.45), -5px -5px 10px rgba(255,255,255,0.85)',
              borderRadius: 6,
              background: '#F8F9FA',
              color: COLORS.text,
              cursor: isLoading ? 'not-allowed' : 'pointer',
              fontSize: 14,
              fontWeight: 600,
              opacity: isLoading ? 0.5 : 1,
            }}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isLoading}
            style={{
              padding: '10px 20px',
              border: 'none',
              borderRadius: 6,
              background: COLORS.primary,
              color: COLORS.white,
              cursor: isLoading ? 'not-allowed' : 'pointer',
              fontSize: 14,
              fontWeight: 600,
              opacity: isLoading ? 0.5 : 1,
            }}
          >
            {isLoading ? 'Saving...' : 'Save Protocol'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

// Add Comparison Modal
function AddComparisonModal({ isOpen, onClose, onSave, isLoading, protocolOptions }) {
  const [formData, setFormData] = useState({
    labName: '',
    labLocation: '',
    cellType: 'MSC',
    efficacyScore: 7,
    safetyScore: 8,
    viabilityPercent: 85,
    sampleSize: 10,
    notes: '',
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
    setFormData({
      labName: '',
      labLocation: '',
      cellType: 'MSC',
      efficacyScore: 7,
      safetyScore: 8,
      viabilityPercent: 85,
      sampleSize: 10,
      notes: '',
    });
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Add Comparative Analysis" isLoading={isLoading}>
      <form onSubmit={handleSubmit}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: COLORS.dark, marginBottom: 6 }}>
              Lab Name
            </label>
            <input
              type="text"
              name="labName"
              value={formData.labName}
              onChange={handleChange}
              style={{
                width: '100%',
                padding: 10,
                boxShadow: 'inset 2px 2px 4px rgba(186,199,216,0.45), inset -2px -2px 4px rgba(255,255,255,0.85)',
                borderRadius: 6,
                fontSize: 13,
                fontFamily: 'inherit',
              }}
            />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: COLORS.dark, marginBottom: 6 }}>
              Location
            </label>
            <input
              type="text"
              name="labLocation"
              value={formData.labLocation}
              onChange={handleChange}
              style={{
                width: '100%',
                padding: 10,
                boxShadow: 'inset 2px 2px 4px rgba(186,199,216,0.45), inset -2px -2px 4px rgba(255,255,255,0.85)',
                borderRadius: 6,
                fontSize: 13,
                fontFamily: 'inherit',
              }}
            />
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: COLORS.dark, marginBottom: 6 }}>
              Cell Type
            </label>
            <select
              name="cellType"
              value={formData.cellType}
              onChange={handleChange}
              style={{
                width: '100%',
                padding: 10,
                boxShadow: 'inset 2px 2px 4px rgba(186,199,216,0.45), inset -2px -2px 4px rgba(255,255,255,0.85)',
                borderRadius: 6,
                fontSize: 13,
                fontFamily: 'inherit',
              }}
            >
              {CELL_TYPE_OPTIONS.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: COLORS.dark, marginBottom: 6 }}>
              Efficacy Score (0-10)
            </label>
            <input
              type="number"
              name="efficacyScore"
              min="0"
              max="10"
              value={formData.efficacyScore}
              onChange={handleChange}
              style={{
                width: '100%',
                padding: 10,
                boxShadow: 'inset 2px 2px 4px rgba(186,199,216,0.45), inset -2px -2px 4px rgba(255,255,255,0.85)',
                borderRadius: 6,
                fontSize: 13,
                fontFamily: 'inherit',
              }}
            />
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: COLORS.dark, marginBottom: 6 }}>
              Safety Score (0-10)
            </label>
            <input
              type="number"
              name="safetyScore"
              min="0"
              max="10"
              value={formData.safetyScore}
              onChange={handleChange}
              style={{
                width: '100%',
                padding: 10,
                boxShadow: 'inset 2px 2px 4px rgba(186,199,216,0.45), inset -2px -2px 4px rgba(255,255,255,0.85)',
                borderRadius: 6,
                fontSize: 13,
                fontFamily: 'inherit',
              }}
            />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: COLORS.dark, marginBottom: 6 }}>
              Viability %
            </label>
            <input
              type="number"
              name="viabilityPercent"
              min="0"
              max="100"
              value={formData.viabilityPercent}
              onChange={handleChange}
              style={{
                width: '100%',
                padding: 10,
                boxShadow: 'inset 2px 2px 4px rgba(186,199,216,0.45), inset -2px -2px 4px rgba(255,255,255,0.85)',
                borderRadius: 6,
                fontSize: 13,
                fontFamily: 'inherit',
              }}
            />
          </div>
        </div>

        <div style={{ marginBottom: 24 }}>
          <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: COLORS.dark, marginBottom: 6 }}>
            Sample Size
          </label>
          <input
            type="number"
            name="sampleSize"
            value={formData.sampleSize}
            onChange={handleChange}
            style={{
              width: '100%',
              padding: 10,
              boxShadow: 'inset 2px 2px 4px rgba(186,199,216,0.45), inset -2px -2px 4px rgba(255,255,255,0.85)',
              borderRadius: 6,
              fontSize: 13,
              fontFamily: 'inherit',
            }}
          />
        </div>

        <div style={{ marginBottom: 24 }}>
          <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: COLORS.dark, marginBottom: 6 }}>
            Notes
          </label>
          <textarea
            name="notes"
            value={formData.notes}
            onChange={handleChange}
            rows={3}
            style={{
              width: '100%',
              padding: 10,
              boxShadow: 'inset 2px 2px 4px rgba(186,199,216,0.45), inset -2px -2px 4px rgba(255,255,255,0.85)',
              borderRadius: 6,
              fontSize: 13,
              fontFamily: 'inherit',
              resize: 'vertical',
            }}
          />
        </div>

        <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
          <button
            onClick={onClose}
            disabled={isLoading}
            style={{
              padding: '10px 20px',
              boxShadow: '5px 5px 10px rgba(186,199,216,0.45), -5px -5px 10px rgba(255,255,255,0.85)',
              borderRadius: 6,
              background: '#F8F9FA',
              color: COLORS.text,
              cursor: isLoading ? 'not-allowed' : 'pointer',
              fontSize: 14,
              fontWeight: 600,
              opacity: isLoading ? 0.5 : 1,
            }}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isLoading}
            style={{
              padding: '10px 20px',
              border: 'none',
              borderRadius: 6,
              background: COLORS.primary,
              color: COLORS.white,
              cursor: isLoading ? 'not-allowed' : 'pointer',
              fontSize: 14,
              fontWeight: 600,
              opacity: isLoading ? 0.5 : 1,
            }}
          >
            {isLoading ? 'Saving...' : 'Add Comparison'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

// Log Treatment Modal
function LogTreatmentModal({ isOpen, onClose, onSave, isLoading, protocolOptions, patientOptions }) {
  const [formData, setFormData] = useState({
    patientId: '',
    protocolId: '',
    treatmentDate: '',
    dose: '',
    route: '',
    outcome: 'Pending',
    followUpDate: '',
    notes: '',
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
    setFormData({
      patientId: '',
      protocolId: '',
      treatmentDate: '',
      dose: '',
      route: '',
      outcome: 'Pending',
      followUpDate: '',
      notes: '',
    });
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Log Treatment" isLoading={isLoading}>
      <form onSubmit={handleSubmit}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: COLORS.dark, marginBottom: 6 }}>
              Patient ID
            </label>
            <input
              type="text"
              name="patientId"
              value={formData.patientId}
              onChange={handleChange}
              style={{
                width: '100%',
                padding: 10,
                boxShadow: 'inset 2px 2px 4px rgba(186,199,216,0.45), inset -2px -2px 4px rgba(255,255,255,0.85)',
                borderRadius: 6,
                fontSize: 13,
                fontFamily: 'inherit',
              }}
            />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: COLORS.dark, marginBottom: 6 }}>
              Protocol
            </label>
            <select
              name="protocolId"
              value={formData.protocolId}
              onChange={handleChange}
              style={{
                width: '100%',
                padding: 10,
                boxShadow: 'inset 2px 2px 4px rgba(186,199,216,0.45), inset -2px -2px 4px rgba(255,255,255,0.85)',
                borderRadius: 6,
                fontSize: 13,
                fontFamily: 'inherit',
              }}
            >
              <option value="">Select a protocol</option>
              {protocolOptions.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.protocolName}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: COLORS.dark, marginBottom: 6 }}>
              Treatment Date
            </label>
            <input
              type="date"
              name="treatmentDate"
              value={formData.treatmentDate}
              onChange={handleChange}
              style={{
                width: '100%',
                padding: 10,
                boxShadow: 'inset 2px 2px 4px rgba(186,199,216,0.45), inset -2px -2px 4px rgba(255,255,255,0.85)',
                borderRadius: 6,
                fontSize: 13,
                fontFamily: 'inherit',
              }}
            />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: COLORS.dark, marginBottom: 6 }}>
              Dose
            </label>
            <input
              type="text"
              name="dose"
              value={formData.dose}
              onChange={handleChange}
              style={{
                width: '100%',
                padding: 10,
                boxShadow: 'inset 2px 2px 4px rgba(186,199,216,0.45), inset -2px -2px 4px rgba(255,255,255,0.85)',
                borderRadius: 6,
                fontSize: 13,
                fontFamily: 'inherit',
              }}
            />
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: COLORS.dark, marginBottom: 6 }}>
              Route
            </label>
            <input
              type="text"
              name="route"
              value={formData.route}
              onChange={handleChange}
              placeholder="e.g., IV, SC, IM"
              style={{
                width: '100%',
                padding: 10,
                boxShadow: 'inset 2px 2px 4px rgba(186,199,216,0.45), inset -2px -2px 4px rgba(255,255,255,0.85)',
                borderRadius: 6,
                fontSize: 13,
                fontFamily: 'inherit',
              }}
            />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: COLORS.dark, marginBottom: 6 }}>
              Outcome
            </label>
            <select
              name="outcome"
              value={formData.outcome}
              onChange={handleChange}
              style={{
                width: '100%',
                padding: 10,
                boxShadow: 'inset 2px 2px 4px rgba(186,199,216,0.45), inset -2px -2px 4px rgba(255,255,255,0.85)',
                borderRadius: 6,
                fontSize: 13,
                fontFamily: 'inherit',
              }}
            >
              {OUTCOME_OPTIONS.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div style={{ marginBottom: 24 }}>
          <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: COLORS.dark, marginBottom: 6 }}>
            Follow-up Date
          </label>
          <input
            type="date"
            name="followUpDate"
            value={formData.followUpDate}
            onChange={handleChange}
            style={{
              width: '100%',
              padding: 10,
              boxShadow: 'inset 2px 2px 4px rgba(186,199,216,0.45), inset -2px -2px 4px rgba(255,255,255,0.85)',
              borderRadius: 6,
              fontSize: 13,
              fontFamily: 'inherit',
            }}
          />
        </div>

        <div style={{ marginBottom: 24 }}>
          <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: COLORS.dark, marginBottom: 6 }}>
            Notes
          </label>
          <textarea
            name="notes"
            value={formData.notes}
            onChange={handleChange}
            rows={3}
            style={{
              width: '100%',
              padding: 10,
              boxShadow: 'inset 2px 2px 4px rgba(186,199,216,0.45), inset -2px -2px 4px rgba(255,255,255,0.85)',
              borderRadius: 6,
              fontSize: 13,
              fontFamily: 'inherit',
              resize: 'vertical',
            }}
          />
        </div>

        <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
          <button
            onClick={onClose}
            disabled={isLoading}
            style={{
              padding: '10px 20px',
              boxShadow: '5px 5px 10px rgba(186,199,216,0.45), -5px -5px 10px rgba(255,255,255,0.85)',
              borderRadius: 6,
              background: '#F8F9FA',
              color: COLORS.text,
              cursor: isLoading ? 'not-allowed' : 'pointer',
              fontSize: 14,
              fontWeight: 600,
              opacity: isLoading ? 0.5 : 1,
            }}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isLoading}
            style={{
              padding: '10px 20px',
              border: 'none',
              borderRadius: 6,
              background: COLORS.primary,
              color: COLORS.white,
              cursor: isLoading ? 'not-allowed' : 'pointer',
              fontSize: 14,
              fontWeight: 600,
              opacity: isLoading ? 0.5 : 1,
            }}
          >
            {isLoading ? 'Saving...' : 'Log Treatment'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

// Published Research Modal
function PublishedResearchModal({ isOpen, onClose, onSave, isLoading }) {
  const [formData, setFormData] = useState({
    title: '',
    authors: '',
    journal: '',
    publicationDate: '',
    doi: '',
    pmid: '',
    nctNumber: '',
    cellType: 'MSC',
    sourceTissue: '',
    conditionTreated: '',
    deliveryMethod: '',
    dosageRange: '',
    sampleSize: '',
    studyPhase: 'Preclinical',
    outcomeSummary: '',
    efficacyRating: 5,
    safetyRating: 5,
    followUpMonths: 12,
    keyFindings: '',
    limitations: '',
    tags: '',
    source: 'Manual',
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
    setFormData({
      title: '',
      authors: '',
      journal: '',
      publicationDate: '',
      doi: '',
      pmid: '',
      nctNumber: '',
      cellType: 'MSC',
      sourceTissue: '',
      conditionTreated: '',
      deliveryMethod: '',
      dosageRange: '',
      sampleSize: '',
      studyPhase: 'Preclinical',
      outcomeSummary: '',
      efficacyRating: 5,
      safetyRating: 5,
      followUpMonths: 12,
      keyFindings: '',
      limitations: '',
      tags: '',
      source: 'Manual',
    });
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Add Published Research" isLoading={isLoading}>
      <form onSubmit={handleSubmit}>
        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: COLORS.dark, marginBottom: 6 }}>
            Title *
          </label>
          <input
            type="text"
            name="title"
            value={formData.title}
            onChange={handleChange}
            style={{
              width: '100%',
              padding: 10,
              boxShadow: 'inset 2px 2px 4px rgba(186,199,216,0.45), inset -2px -2px 4px rgba(255,255,255,0.85)',
              borderRadius: 6,
              fontSize: 13,
              fontFamily: 'inherit',
            }}
          />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: COLORS.dark, marginBottom: 6 }}>
              Authors
            </label>
            <input
              type="text"
              name="authors"
              value={formData.authors}
              onChange={handleChange}
              style={{
                width: '100%',
                padding: 10,
                boxShadow: 'inset 2px 2px 4px rgba(186,199,216,0.45), inset -2px -2px 4px rgba(255,255,255,0.85)',
                borderRadius: 6,
                fontSize: 13,
                fontFamily: 'inherit',
              }}
            />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: COLORS.dark, marginBottom: 6 }}>
              Journal
            </label>
            <input
              type="text"
              name="journal"
              value={formData.journal}
              onChange={handleChange}
              style={{
                width: '100%',
                padding: 10,
                boxShadow: 'inset 2px 2px 4px rgba(186,199,216,0.45), inset -2px -2px 4px rgba(255,255,255,0.85)',
                borderRadius: 6,
                fontSize: 13,
                fontFamily: 'inherit',
              }}
            />
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16, marginBottom: 16 }}>
          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: COLORS.dark, marginBottom: 6 }}>
              Publication Date
            </label>
            <input
              type="date"
              name="publicationDate"
              value={formData.publicationDate}
              onChange={handleChange}
              style={{
                width: '100%',
                padding: 10,
                boxShadow: 'inset 2px 2px 4px rgba(186,199,216,0.45), inset -2px -2px 4px rgba(255,255,255,0.85)',
                borderRadius: 6,
                fontSize: 13,
                fontFamily: 'inherit',
              }}
            />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: COLORS.dark, marginBottom: 6 }}>
              DOI
            </label>
            <input
              type="text"
              name="doi"
              value={formData.doi}
              onChange={handleChange}
              style={{
                width: '100%',
                padding: 10,
                boxShadow: 'inset 2px 2px 4px rgba(186,199,216,0.45), inset -2px -2px 4px rgba(255,255,255,0.85)',
                borderRadius: 6,
                fontSize: 13,
                fontFamily: 'inherit',
              }}
            />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: COLORS.dark, marginBottom: 6 }}>
              PMID
            </label>
            <input
              type="text"
              name="pmid"
              value={formData.pmid}
              onChange={handleChange}
              style={{
                width: '100%',
                padding: 10,
                boxShadow: 'inset 2px 2px 4px rgba(186,199,216,0.45), inset -2px -2px 4px rgba(255,255,255,0.85)',
                borderRadius: 6,
                fontSize: 13,
                fontFamily: 'inherit',
              }}
            />
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: COLORS.dark, marginBottom: 6 }}>
              NCT Number
            </label>
            <input
              type="text"
              name="nctNumber"
              value={formData.nctNumber}
              onChange={handleChange}
              style={{
                width: '100%',
                padding: 10,
                boxShadow: 'inset 2px 2px 4px rgba(186,199,216,0.45), inset -2px -2px 4px rgba(255,255,255,0.85)',
                borderRadius: 6,
                fontSize: 13,
                fontFamily: 'inherit',
              }}
            />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: COLORS.dark, marginBottom: 6 }}>
              Cell Type
            </label>
            <select
              name="cellType"
              value={formData.cellType}
              onChange={handleChange}
              style={{
                width: '100%',
                padding: 10,
                boxShadow: 'inset 2px 2px 4px rgba(186,199,216,0.45), inset -2px -2px 4px rgba(255,255,255,0.85)',
                borderRadius: 6,
                fontSize: 13,
                fontFamily: 'inherit',
              }}
            >
              {CELL_TYPE_OPTIONS.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: COLORS.dark, marginBottom: 6 }}>
              Source Tissue
            </label>
            <input
              type="text"
              name="sourceTissue"
              value={formData.sourceTissue}
              onChange={handleChange}
              style={{
                width: '100%',
                padding: 10,
                boxShadow: 'inset 2px 2px 4px rgba(186,199,216,0.45), inset -2px -2px 4px rgba(255,255,255,0.85)',
                borderRadius: 6,
                fontSize: 13,
                fontFamily: 'inherit',
              }}
            />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: COLORS.dark, marginBottom: 6 }}>
              Condition Treated
            </label>
            <input
              type="text"
              name="conditionTreated"
              value={formData.conditionTreated}
              onChange={handleChange}
              style={{
                width: '100%',
                padding: 10,
                boxShadow: 'inset 2px 2px 4px rgba(186,199,216,0.45), inset -2px -2px 4px rgba(255,255,255,0.85)',
                borderRadius: 6,
                fontSize: 13,
                fontFamily: 'inherit',
              }}
            />
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: COLORS.dark, marginBottom: 6 }}>
              Delivery Method
            </label>
            <input
              type="text"
              name="deliveryMethod"
              value={formData.deliveryMethod}
              onChange={handleChange}
              style={{
                width: '100%',
                padding: 10,
                boxShadow: 'inset 2px 2px 4px rgba(186,199,216,0.45), inset -2px -2px 4px rgba(255,255,255,0.85)',
                borderRadius: 6,
                fontSize: 13,
                fontFamily: 'inherit',
              }}
            />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: COLORS.dark, marginBottom: 6 }}>
              Dosage Range
            </label>
            <input
              type="text"
              name="dosageRange"
              value={formData.dosageRange}
              onChange={handleChange}
              style={{
                width: '100%',
                padding: 10,
                boxShadow: 'inset 2px 2px 4px rgba(186,199,216,0.45), inset -2px -2px 4px rgba(255,255,255,0.85)',
                borderRadius: 6,
                fontSize: 13,
                fontFamily: 'inherit',
              }}
            />
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: COLORS.dark, marginBottom: 6 }}>
              Sample Size
            </label>
            <input
              type="text"
              name="sampleSize"
              value={formData.sampleSize}
              onChange={handleChange}
              style={{
                width: '100%',
                padding: 10,
                boxShadow: 'inset 2px 2px 4px rgba(186,199,216,0.45), inset -2px -2px 4px rgba(255,255,255,0.85)',
                borderRadius: 6,
                fontSize: 13,
                fontFamily: 'inherit',
              }}
            />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: COLORS.dark, marginBottom: 6 }}>
              Study Phase
            </label>
            <select
              name="studyPhase"
              value={formData.studyPhase}
              onChange={handleChange}
              style={{
                width: '100%',
                padding: 10,
                boxShadow: 'inset 2px 2px 4px rgba(186,199,216,0.45), inset -2px -2px 4px rgba(255,255,255,0.85)',
                borderRadius: 6,
                fontSize: 13,
                fontFamily: 'inherit',
              }}
            >
              {STUDY_PHASE_OPTIONS.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: COLORS.dark, marginBottom: 6 }}>
              Efficacy Rating (0-10)
            </label>
            <input
              type="number"
              name="efficacyRating"
              min="0"
              max="10"
              value={formData.efficacyRating}
              onChange={handleChange}
              style={{
                width: '100%',
                padding: 10,
                boxShadow: 'inset 2px 2px 4px rgba(186,199,216,0.45), inset -2px -2px 4px rgba(255,255,255,0.85)',
                borderRadius: 6,
                fontSize: 13,
                fontFamily: 'inherit',
              }}
            />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: COLORS.dark, marginBottom: 6 }}>
              Safety Rating (0-10)
            </label>
            <input
              type="number"
              name="safetyRating"
              min="0"
              max="10"
              value={formData.safetyRating}
              onChange={handleChange}
              style={{
                width: '100%',
                padding: 10,
                boxShadow: 'inset 2px 2px 4px rgba(186,199,216,0.45), inset -2px -2px 4px rgba(255,255,255,0.85)',
                borderRadius: 6,
                fontSize: 13,
                fontFamily: 'inherit',
              }}
            />
          </div>
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: COLORS.dark, marginBottom: 6 }}>
            Follow-up Months
          </label>
          <input
            type="number"
            name="followUpMonths"
            value={formData.followUpMonths}
            onChange={handleChange}
            style={{
              width: '100%',
              padding: 10,
              boxShadow: 'inset 2px 2px 4px rgba(186,199,216,0.45), inset -2px -2px 4px rgba(255,255,255,0.85)',
              borderRadius: 6,
              fontSize: 13,
              fontFamily: 'inherit',
            }}
          />
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: COLORS.dark, marginBottom: 6 }}>
            Outcome Summary
          </label>
          <textarea
            name="outcomeSummary"
            value={formData.outcomeSummary}
            onChange={handleChange}
            rows={2}
            style={{
              width: '100%',
              padding: 10,
              boxShadow: 'inset 2px 2px 4px rgba(186,199,216,0.45), inset -2px -2px 4px rgba(255,255,255,0.85)',
              borderRadius: 6,
              fontSize: 13,
              fontFamily: 'inherit',
              resize: 'vertical',
            }}
          />
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: COLORS.dark, marginBottom: 6 }}>
            Key Findings
          </label>
          <textarea
            name="keyFindings"
            value={formData.keyFindings}
            onChange={handleChange}
            rows={2}
            style={{
              width: '100%',
              padding: 10,
              boxShadow: 'inset 2px 2px 4px rgba(186,199,216,0.45), inset -2px -2px 4px rgba(255,255,255,0.85)',
              borderRadius: 6,
              fontSize: 13,
              fontFamily: 'inherit',
              resize: 'vertical',
            }}
          />
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: COLORS.dark, marginBottom: 6 }}>
            Limitations
          </label>
          <textarea
            name="limitations"
            value={formData.limitations}
            onChange={handleChange}
            rows={2}
            style={{
              width: '100%',
              padding: 10,
              boxShadow: 'inset 2px 2px 4px rgba(186,199,216,0.45), inset -2px -2px 4px rgba(255,255,255,0.85)',
              borderRadius: 6,
              fontSize: 13,
              fontFamily: 'inherit',
              resize: 'vertical',
            }}
          />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 24 }}>
          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: COLORS.dark, marginBottom: 6 }}>
              Tags
            </label>
            <input
              type="text"
              name="tags"
              value={formData.tags}
              onChange={handleChange}
              placeholder="comma-separated"
              style={{
                width: '100%',
                padding: 10,
                boxShadow: 'inset 2px 2px 4px rgba(186,199,216,0.45), inset -2px -2px 4px rgba(255,255,255,0.85)',
                borderRadius: 6,
                fontSize: 13,
                fontFamily: 'inherit',
              }}
            />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: COLORS.dark, marginBottom: 6 }}>
              Source
            </label>
            <select
              name="source"
              value={formData.source}
              onChange={handleChange}
              style={{
                width: '100%',
                padding: 10,
                boxShadow: 'inset 2px 2px 4px rgba(186,199,216,0.45), inset -2px -2px 4px rgba(255,255,255,0.85)',
                borderRadius: 6,
                fontSize: 13,
                fontFamily: 'inherit',
              }}
            >
              <option value="Manual">Manual</option>
              <option value="PubMed">PubMed</option>
              <option value="ClinicalTrials.gov">ClinicalTrials.gov</option>
              <option value="Import">Import</option>
              <option value="AI Curated">AI Curated</option>
            </select>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
          <button
            onClick={onClose}
            disabled={isLoading}
            style={{
              padding: '10px 20px',
              boxShadow: '5px 5px 10px rgba(186,199,216,0.45), -5px -5px 10px rgba(255,255,255,0.85)',
              borderRadius: 6,
              background: '#F8F9FA',
              color: COLORS.text,
              cursor: isLoading ? 'not-allowed' : 'pointer',
              fontSize: 14,
              fontWeight: 600,
              opacity: isLoading ? 0.5 : 1,
            }}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isLoading}
            style={{
              padding: '10px 20px',
              border: 'none',
              borderRadius: 6,
              background: COLORS.primary,
              color: COLORS.white,
              cursor: isLoading ? 'not-allowed' : 'pointer',
              fontSize: 14,
              fontWeight: 600,
              opacity: isLoading ? 0.5 : 1,
            }}
          >
            {isLoading ? 'Saving...' : 'Add Research'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

// Score Bar Component
function ScoreBar({ score }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <div style={{ width: 60, height: 4, background: COLORS.border, borderRadius: 2, overflow: 'hidden' }}>
        <div
          style={{
            height: '100%',
            width: `${score * 10}%`,
            background: score >= 7 ? COLORS.green : score >= 5 ? COLORS.orange : COLORS.red,
          }}
        />
      </div>
      <span style={{ fontSize: 12, fontWeight: 600, color: COLORS.text, minWidth: 20 }}>{score}/10</span>
    </div>
  );
}

// Protocol Detail Modal
function ProtocolDetailModal({ isOpen, onClose, protocol }) {
  const [detailTab, setDetailTab] = useState('overview');

  if (!isOpen || !protocol) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={`${protocol.protocolName} (${protocol.protocolNumber})`}>
      <div style={{ marginBottom: 16, borderBottom: `1px solid ${COLORS.border}`, display: 'flex', gap: 16 }}>
        {['overview', 'labData', 'comparisons', 'treatments'].map((tab) => (
          <button
            key={tab}
            onClick={() => setDetailTab(tab)}
            style={{
              padding: '8px 12px',
              border: 'none',
              background: 'none',
              color: detailTab === tab ? COLORS.primary : COLORS.textLight,
              borderBottom: detailTab === tab ? `2px solid ${COLORS.primary}` : 'none',
              cursor: 'pointer',
              fontSize: 13,
              fontWeight: detailTab === tab ? 600 : 400,
            }}
          >
            {tab === 'overview' && 'Overview'}
            {tab === 'labData' && 'Lab Data'}
            {tab === 'comparisons' && 'Comparisons'}
            {tab === 'treatments' && 'Treatments'}
          </button>
        ))}
      </div>

      {detailTab === 'overview' && (
        <div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
            <div>
              <h4 style={{ fontSize: 13, fontWeight: 600, color: COLORS.dark, marginBottom: 8 }}>
                Cell Type Information
              </h4>
              <div style={{ fontSize: 12, color: COLORS.text, lineHeight: 1.8 }}>
                <div>
                  <strong>Cell Type:</strong> {protocol.cellType || 'N/A'}
                </div>
                <div>
                  <strong>Category:</strong> {protocol.category || 'N/A'}
                </div>
                <div>
                  <strong>Regulatory Status:</strong> {protocol.regulatoryStatus || 'N/A'}
                </div>
                <div>
                  <strong>Source Tissue:</strong> {protocol.sourceTissue || 'N/A'}
                </div>
                <div>
                  <strong>Isolation Method:</strong> {protocol.isolationMethod || 'N/A'}
                </div>
                <div>
                  <strong>Culture Medium:</strong> {protocol.cultureMedium || 'N/A'}
                </div>
              </div>
            </div>
            <div>
              <h4 style={{ fontSize: 13, fontWeight: 600, color: COLORS.dark, marginBottom: 8 }}>
                Culture Parameters
              </h4>
              <div style={{ fontSize: 12, color: COLORS.text, lineHeight: 1.8 }}>
                <div>
                  <strong>Passage Range:</strong> {protocol.passageRange || 'N/A'}
                </div>
                <div>
                  <strong>Viability Threshold:</strong> {protocol.viabilityThreshold || 'N/A'}%
                </div>
                <div>
                  <strong>Differentiation Markers:</strong> {protocol.differentiationMarkers || 'N/A'}
                </div>
                <div>
                  <strong>Growth Factors:</strong> {protocol.growthFactors || 'N/A'}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {detailTab === 'labData' && (
        <div>
          <h4 style={{ fontSize: 13, fontWeight: 600, color: COLORS.dark, marginBottom: 12 }}>
            Culture Conditions
          </h4>
          <div
            style={{
              padding: 12,
              background: COLORS.light,
              borderRadius: 6,
              fontSize: 12,
              color: COLORS.text,
              whiteSpace: 'pre-wrap',
              marginBottom: 16,
            }}
          >
            {protocol.cultureConditions || 'No conditions specified'}
          </div>
          <h4 style={{ fontSize: 13, fontWeight: 600, color: COLORS.dark, marginBottom: 12 }}>
            Safety Profile
          </h4>
          <div
            style={{
              padding: 12,
              background: COLORS.light,
              borderRadius: 6,
              fontSize: 12,
              color: COLORS.text,
              whiteSpace: 'pre-wrap',
            }}
          >
            {protocol.safetyProfile || 'No safety information specified'}
          </div>
        </div>
      )}

      {detailTab === 'comparisons' && (
        <div>
          <p style={{ color: COLORS.textLight, fontSize: 12 }}>
            Comparative analyses linked to this protocol will appear here
          </p>
        </div>
      )}

      {detailTab === 'treatments' && (
        <div>
          <p style={{ color: COLORS.textLight, fontSize: 12 }}>
            Patient treatments using this protocol will appear here
          </p>
        </div>
      )}
    </Modal>
  );
}

// Main BiolabPage Component
export default function BiolabPage({ user }) {
  const [activeTab, setActiveTab] = useState('protocols');
  const [summary, setSummary] = useState({
    totalProtocols: 0,
    totalComparisons: 0,
    totalTreatments: 0,
    byCategory: {},
  });
  const [protocols, setProtocols] = useState([]);
  const [comparisons, setComparisons] = useState([]);
  const [treatments, setTreatments] = useState([]);
  const [publishedResearch, setPublishedResearch] = useState([]);
  const [aiAnalyses, setAiAnalyses] = useState([]);
  const [intelligenceSummary, setIntelligenceSummary] = useState({});
  const [loading, setLoading] = useState(false);
  const [isNewProtocolOpen, setIsNewProtocolOpen] = useState(false);
  const [isComparisonOpen, setIsComparisonOpen] = useState(false);
  const [isTreatmentOpen, setIsTreatmentOpen] = useState(false);
  const [isPublishedResearchOpen, setIsPublishedResearchOpen] = useState(false);
  const [expandedProtocol, setExpandedProtocol] = useState(null);

  // Filter states
  const [categoryFilter, setCategoryFilter] = useState('All');
  const [cellTypeFilter, setCellTypeFilter] = useState('All');
  const [searchTerm, setSearchTerm] = useState('');

  // Published Research filter states
  const [researchCellTypeFilter, setResearchCellTypeFilter] = useState('All');
  const [researchPhaseFilter, setResearchPhaseFilter] = useState('All');
  const [researchSearchTerm, setResearchSearchTerm] = useState('');

  // AI Analysis states
  const [analysisType, setAnalysisType] = useState('protocol_comparison');
  const [conditionFilter, setConditionFilter] = useState('');
  const [queryText, setQueryText] = useState('');
  const [analysisResult, setAnalysisResult] = useState(null);
  const [analysisLoading, setAnalysisLoading] = useState(false);
  const [showAnalysisHistory, setShowAnalysisHistory] = useState(false);

  // Data Ingestion state
  const [ingestionSource, setIngestionSource] = useState('PubMed');
  const [ingestionQuery, setIngestionQuery] = useState('');
  const [ingestionResults, setIngestionResults] = useState([]);
  const [ingestionHistory, setIngestionHistory] = useState([]);
  const [ingestionLoading, setIngestionLoading] = useState(false);
  const [selectedImports, setSelectedImports] = useState(new Set());

  // Doctor Dashboard state
  const [similarProtocols, setSimilarProtocols] = useState([]);
  const [selectedProtocolA, setSelectedProtocolA] = useState('');
  const [selectedProtocolB, setSelectedProtocolB] = useState('');
  const [similarityResult, setSimilarityResult] = useState(null);
  const [recommendations, setRecommendations] = useState([]);
  const [recsLoading, setRecsLoading] = useState(false);
  const [selectedRecProtocol, setSelectedRecProtocol] = useState('');

  // Compliance state
  const [signatures, setSignatures] = useState([]);
  const [signatureVerification, setSignatureVerification] = useState(null);

  // Document Upload state
  const [documents, setDocuments] = useState([]);
  const [docsSummary, setDocsSummary] = useState({ totalDocuments: 0, totalAnalyses: 0, byType: [], byCategory: [], recentUploads: [] });
  const [docSearchTerm, setDocSearchTerm] = useState('');
  const [docTypeFilter, setDocTypeFilter] = useState('All');
  const [docCategoryFilter, setDocCategoryFilter] = useState('All');
  const [docUploading, setDocUploading] = useState(false);
  const [docUploadResults, setDocUploadResults] = useState(null);
  const [recentLocations, setRecentLocations] = useState([]);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [docAnalysisType, setDocAnalysisType] = useState('key_findings');
  const [docAnalysisQuery, setDocAnalysisQuery] = useState('');
  const [docAnalysisResult, setDocAnalysisResult] = useState(null);
  const [docAnalysisLoading, setDocAnalysisLoading] = useState(false);
  const [docAnalysisHistory, setDocAnalysisHistory] = useState([]);
  const [showDocAnalysisHistory, setShowDocAnalysisHistory] = useState(false);
  const [pathInput, setPathInput] = useState('');
  const [pathValidation, setPathValidation] = useState(null);

  // Web upload: hidden file input ref, web mode detection, and staged files
  const fileInputRef = useRef(null);
  const isWebMode = typeof window !== 'undefined' && !window.electronAPI;
  const [stagedFiles, setStagedFiles] = useState([]);
  const [dragActive, setDragActive] = useState(false);
  const dropZoneRef = useRef(null);
  // Staged paths: for Electron/network upload queue (separate from web file objects)
  const [stagedPaths, setStagedPaths] = useState([]);

  // Load data on mount
  useEffect(() => {
    loadData();
  }, []);

  const loadData = useCallback(async () => {
    if (!window.zignagenixAPI) {
      console.error('ZignaGenix API not available');
      return;
    }

    setLoading(true);
    try {
      const [summaryData, protocolsData, publishedResearchData, aiAnalysesData, intelligenceSummaryData] = await Promise.all([
        window.zignagenixAPI.getBiolabSummary(),
        window.zignagenixAPI.getBiolabProtocols({}),
        window.zignagenixAPI.getPublishedResearch({}),
        window.zignagenixAPI.getBiolabAIHistory({}),
        window.zignagenixAPI.getBiolabIntelligenceSummary(),
      ]);

      setSummary(summaryData || {});
      setProtocols(Array.isArray(protocolsData) ? protocolsData : []);
      setPublishedResearch(Array.isArray(publishedResearchData) ? publishedResearchData : []);
      setAiAnalyses(Array.isArray(aiAnalysesData) ? aiAnalysesData : []);
      setIntelligenceSummary(intelligenceSummaryData || {});
    } catch (error) {
      console.error('Error loading biolab data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleNewProtocol = async (formData) => {
    if (!window.zignagenixAPI) {
      console.error('ZignaGenix API not available');
      return;
    }

    setLoading(true);
    try {
      const result = await window.zignagenixAPI.createBiolabProtocol(formData);
      if (result) {
        setProtocols([...protocols, result]);
        setIsNewProtocolOpen(false);
        // Reload summary
        const newSummary = await window.zignagenixAPI.getBiolabSummary();
        setSummary(newSummary || {});
      }
    } catch (error) {
      console.error('Error creating protocol:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddComparison = async (formData) => {
    if (!window.zignagenixAPI) {
      console.error('ZignaGenix API not available');
      return;
    }

    setLoading(true);
    try {
      const result = await window.zignagenixAPI.createBiolabComparison(formData);
      if (result) {
        setComparisons([...comparisons, result]);
        setIsComparisonOpen(false);
        // Reload summary
        const newSummary = await window.zignagenixAPI.getBiolabSummary();
        setSummary(newSummary || {});
      }
    } catch (error) {
      console.error('Error adding comparison:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogTreatment = async (formData) => {
    if (!window.zignagenixAPI) {
      console.error('ZignaGenix API not available');
      return;
    }

    setLoading(true);
    try {
      const result = await window.zignagenixAPI.createBiolabTreatment(formData);
      if (result) {
        setTreatments([...treatments, result]);
        setIsTreatmentOpen(false);
        // Reload summary
        const newSummary = await window.zignagenixAPI.getBiolabSummary();
        setSummary(newSummary || {});
      }
    } catch (error) {
      console.error('Error logging treatment:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddPublishedResearch = async (formData) => {
    if (!window.zignagenixAPI) {
      console.error('ZignaGenix API not available');
      return;
    }

    setLoading(true);
    try {
      const result = await window.zignagenixAPI.createPublishedResearch(formData);
      if (result) {
        setPublishedResearch([...publishedResearch, result]);
        setIsPublishedResearchOpen(false);
        // Reload intelligence summary
        const newSummary = await window.zignagenixAPI.getBiolabIntelligenceSummary();
        setIntelligenceSummary(newSummary || {});
      }
    } catch (error) {
      console.error('Error adding published research:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRunAIAnalysis = async () => {
    if (!window.zignagenixAPI) {
      console.error('ZignaGenix API not available');
      return;
    }

    if (!queryText.trim()) {
      alert('Please enter a query');
      return;
    }

    // Clear previous result so the user doesn't see stale text while the new
    // Opus call is in flight.
    setAnalysisResult(null);
    setAnalysisLoading(true);
    try {
      const result = await window.zignagenixAPI.runBiolabAIAnalysis({
        analysisType,
        queryText,
        protocolIds: [],
        conditionFilter,
      });
      if (result) {
        setAnalysisResult(result);
        // Reload AI analyses history and intelligence summary in parallel
        // (don't block the user from seeing their result while these refresh).
        Promise.all([
          window.zignagenixAPI.getBiolabAIHistory({}).catch(() => []),
          window.zignagenixAPI.getBiolabIntelligenceSummary().catch(() => ({})),
        ]).then(([newAnalyses, newSummary]) => {
          setAiAnalyses(Array.isArray(newAnalyses) ? newAnalyses : []);
          setIntelligenceSummary(newSummary || {});
        });
      } else {
        setAnalysisResult({ error: true, message: 'No response received from the AI service.' });
      }
    } catch (error) {
      console.error('Error running AI analysis:', error);
      setAnalysisResult({ error: true, message: error?.message || 'AI analysis failed.' });
    } finally {
      setAnalysisLoading(false);
    }
  };

  const handleRecalculateAllScores = async () => {
    if (!window.zignagenixAPI) {
      console.error('ZignaGenix API not available');
      return;
    }

    setLoading(true);
    try {
      const result = await window.zignagenixAPI.recalculateAllProtocolScores();
      if (result) {
        // Reload all data
        await loadData();
      }
    } catch (error) {
      console.error('Error recalculating scores:', error);
    } finally {
      setLoading(false);
    }
  };

  // ── Data Ingestion Handlers ──────────────────────────────────
  const handleIngestionSearch = async () => {
    setIngestionLoading(true);
    try {
      const result = await window.zignagenixAPI.searchExternalResearch({
        source: ingestionSource,
        query: ingestionQuery,
        maxResults: 20,
      });
      if (result?.results) {
        setIngestionResults(result.results);
        setSelectedImports(new Set());
      }
    } catch (err) { console.error('Ingestion search error:', err); }
    setIngestionLoading(false);
  };

  const handleIngestionImport = async () => {
    setIngestionLoading(true);
    try {
      const articles = [...selectedImports].map((idx) => ingestionResults[idx]);
      const result = await window.zignagenixAPI.importExternalResearch({ articles });
      if (result?.success) {
        setIngestionResults([]);
        setSelectedImports(new Set());
        loadIngestionHistory();
        // Refresh published research if on that tab
        loadPublishedResearch();
      }
    } catch (err) { console.error('Import error:', err); }
    setIngestionLoading(false);
  };

  const loadIngestionHistory = async () => {
    try {
      const history = await window.zignagenixAPI.getIngestionHistory();
      setIngestionHistory(Array.isArray(history) ? history : []);
    } catch (err) { console.error('Load history error:', err); }
  };

  // ── Doctor Dashboard Handlers ─────────────────────────────────
  const handleComputeSimilarity = async () => {
    setLoading(true);
    setSimilarityResult(null);
    try {
      const result = await window.zignagenixAPI.computeProtocolSimilarity(
        parseInt(selectedProtocolA), parseInt(selectedProtocolB)
      );
      if (result?.success) {
        setSimilarityResult(result);
      }
    } catch (err) { console.error('Similarity error:', err); }
    setLoading(false);
  };

  const handleGenerateRecommendations = async () => {
    setRecsLoading(true);
    setRecommendations([]);
    try {
      const result = await window.zignagenixAPI.generateRecommendations({
        protocolId: selectedRecProtocol ? parseInt(selectedRecProtocol) : undefined,
      });
      if (result?.recommendations) {
        setRecommendations(result.recommendations);
      }
    } catch (err) { console.error('Recommendations error:', err); }
    setRecsLoading(false);
  };

  // ── Compliance Handlers ───────────────────────────────────────
  const loadSignatures = async (recordType) => {
    setLoading(true);
    try {
      const sigs = await window.zignagenixAPI.getSignaturesByType(recordType);
      setSignatures(Array.isArray(sigs) ? sigs : []);
      if (!Array.isArray(sigs) || sigs.length === 0) {
        setSignatureVerification({ valid: true, reason: `No signatures found for ${recordType.replace('biolab_', '').replace('_', ' ')}. Sign a record first using the E-Signature system.`, signer: 'N/A', role: 'N/A', meaning: 'No records signed yet' });
      } else {
        setSignatureVerification(null);
      }
    } catch (err) {
      console.error('Load signatures error:', err);
      setSignatures([]);
      setSignatureVerification({ valid: false, reason: `Error loading signatures: ${err.message || 'Database table may not exist yet. Run the migration first.'}`, signer: 'N/A', role: 'N/A', meaning: 'Error' });
    }
    setLoading(false);
  };

  const handleVerifySignature = async (signatureId) => {
    try {
      const result = await window.zignagenixAPI.verifySignature(signatureId);
      setSignatureVerification(result);
    } catch (err) { console.error('Verify error:', err); }
  };

  // ── Document Upload & Analysis Handlers ──────────────────────
  const loadDocuments = useCallback(async () => {
    try {
      const filters = {};
      if (docTypeFilter !== 'All') filters.fileType = docTypeFilter;
      if (docCategoryFilter !== 'All') filters.category = docCategoryFilter;
      if (docSearchTerm) filters.search = docSearchTerm;
      const [docs, summary, locations] = await Promise.all([
        window.zignagenixAPI.getBiolabDocuments(filters),
        window.zignagenixAPI.getBiolabDocsSummary(),
        window.zignagenixAPI.getBiolabRecentLocations(),
      ]);
      setDocuments(Array.isArray(docs) ? docs : []);
      if (summary) setDocsSummary(summary);
      setRecentLocations(Array.isArray(locations) ? locations : []);
    } catch (err) { console.error('Load documents error:', err); }
  }, [docTypeFilter, docCategoryFilter, docSearchTerm]);

  useEffect(() => {
    if (activeTab === 'documents') loadDocuments();
  }, [activeTab, loadDocuments]);

  const handleBrowseFiles = async () => {
    if (isWebMode) {
      // Web mode: trigger hidden HTML5 file input (supports multiple selection)
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
        fileInputRef.current.click();
      }
      return;
    }
    // Electron mode: open native multi-select dialog, add results to the path queue
    try {
      const result = await window.zignagenixAPI.browseBiolabDocuments({});
      if (result && !result.canceled && result.previews?.length > 0) {
        const newPaths = result.previews
          .filter(p => p.accessible && p.isSupported)
          .map(p => ({
            path: p.path,
            name: p.name,
            ext: p.ext,
            size: p.size,
            isNetwork: p.isNetwork,
            id: `${p.path}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
          }));
        if (newPaths.length > 0) setStagedPaths(prev => {
          // Avoid duplicates
          const existing = new Set(prev.map(p => p.path));
          return [...prev, ...newPaths.filter(p => !existing.has(p.path))];
        });
      }
    } catch (err) { console.error('Browse error:', err); }
  };

  // Web mode: handle files selected from HTML5 file input — stage them
  const handleWebFileSelected = (e) => {
    const selectedFiles = e.target.files;
    if (!selectedFiles || selectedFiles.length === 0) return;
    const newFiles = Array.from(selectedFiles).map(f => ({
      file: f,
      name: f.name,
      size: f.size,
      type: f.name.split('.').pop().toLowerCase(),
      id: `${f.name}-${f.size}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    }));
    setStagedFiles(prev => [...prev, ...newFiles]);
  };

  // Drag-and-drop handlers
  const handleDragEnter = (e) => { e.preventDefault(); e.stopPropagation(); setDragActive(true); };
  const handleDragLeave = (e) => { e.preventDefault(); e.stopPropagation(); if (dropZoneRef.current && !dropZoneRef.current.contains(e.relatedTarget)) setDragActive(false); };
  const handleDragOver = (e) => { e.preventDefault(); e.stopPropagation(); };
  const handleDrop = (e) => {
    e.preventDefault(); e.stopPropagation(); setDragActive(false);
    const droppedFiles = e.dataTransfer?.files;
    if (!droppedFiles || droppedFiles.length === 0) return;
    const ALLOWED = ['.pdf', '.docx', '.doc', '.txt', '.csv', '.xlsx', '.xls', '.rtf'];
    const newFiles = Array.from(droppedFiles)
      .filter(f => {
        const ext = '.' + f.name.split('.').pop().toLowerCase();
        return ALLOWED.includes(ext);
      })
      .map(f => ({
        file: f,
        name: f.name,
        size: f.size,
        type: f.name.split('.').pop().toLowerCase(),
        id: `${f.name}-${f.size}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      }));
    if (newFiles.length > 0) setStagedFiles(prev => [...prev, ...newFiles]);
  };

  // Remove a staged file
  const removeStagedFile = (id) => setStagedFiles(prev => prev.filter(f => f.id !== id));

  // Remove a staged path
  const removeStagedPath = (id) => setStagedPaths(prev => prev.filter(p => p.id !== id));

  // Upload all queued paths (Electron/network mode)
  const uploadStagedPaths = async () => {
    if (stagedPaths.length === 0) return;
    setDocUploading(true);
    setDocUploadResults(null);
    try {
      const result = await window.zignagenixAPI.uploadBiolabDocument({
        filePaths: stagedPaths.map(p => p.path),
      });
      if (result && !result.error) {
        setDocUploadResults(result);
        setStagedPaths([]);
        loadDocuments();
      } else if (result) {
        setDocUploadResults(result);
      }
    } catch (err) { console.error('Staged paths upload error:', err); }
    setDocUploading(false);
  };

  // Upload all staged files — sends one file at a time to avoid exceeding JSON body limits
  const uploadStagedFiles = async () => {
    if (stagedFiles.length === 0) return;
    setDocUploading(true);
    setDocUploadResults(null);
    const allResults = [];
    try {
      for (const sf of stagedFiles) {
        try {
          const base64 = await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => {
              const dataUrl = reader.result;
              const base64Data = dataUrl.split(',')[1];
              resolve(base64Data);
            };
            reader.onerror = reject;
            reader.readAsDataURL(sf.file);
          });
          const result = await window.zignagenixAPI.uploadBiolabDocument({
            files: [{ name: sf.name, data: base64 }],
          });
          if (result && result.documents) {
            allResults.push(...result.documents);
          } else if (result && result.error) {
            allResults.push({ file: sf.name, error: true, message: result.message || 'Upload failed' });
          }
        } catch (fileErr) {
          console.error(`Upload error for ${sf.name}:`, fileErr);
          allResults.push({ file: sf.name, error: true, message: fileErr.message || 'Upload failed' });
        }
      }
      setDocUploadResults({ success: true, documents: allResults });
      const anySuccess = allResults.some(r => r.success);
      if (anySuccess) {
        setStagedFiles([]);
        loadDocuments();
      }
    } catch (err) { console.error('Web upload error:', err); }
    setDocUploading(false);
  };

  const handleBrowseFolder = async () => {
    if (isWebMode) {
      // Web mode: folder scanning not supported, use file picker instead
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
        fileInputRef.current.click();
      }
      return;
    }
    // Electron mode: use native folder dialog via IPC
    setDocUploading(true);
    setDocUploadResults(null);
    try {
      const scan = await window.zignagenixAPI.browseBiolabFolder({});
      if (scan && !scan.canceled && scan.documents?.length > 0) {
        const paths = scan.documents.filter(d => d.withinSizeLimit).map(d => d.path);
        if (paths.length > 0) {
          const result = await window.zignagenixAPI.uploadBiolabDocument({ filePaths: paths });
          setDocUploadResults(result);
          loadDocuments();
        }
      }
    } catch (err) { console.error('Folder upload error:', err); }
    setDocUploading(false);
  };

  const handleUploadFromPath = async () => {
    if (!pathInput.trim()) return;
    setDocUploadResults(null);
    try {
      const validation = await window.zignagenixAPI.validateBiolabDocPath(pathInput.trim());
      setPathValidation(validation);
      if (validation.valid) {
        if (validation.isFile && validation.isSupported) {
          // Add single file to the path queue
          const newEntry = {
            path: pathInput.trim(),
            name: validation.fileName,
            ext: validation.ext,
            size: validation.size,
            isNetwork: validation.isNetwork,
            id: `${pathInput.trim()}-${Date.now()}`,
          };
          setStagedPaths(prev => {
            if (prev.some(p => p.path === newEntry.path)) return prev; // dedupe
            return [...prev, newEntry];
          });
          setPathInput('');
          setPathValidation(null);
        } else if (validation.isDirectory) {
          // Directories upload immediately (they expand to many files server-side)
          setDocUploading(true);
          const result = await window.zignagenixAPI.uploadBiolabDocument({ folderPath: pathInput.trim() });
          setDocUploadResults(result);
          loadDocuments();
          setDocUploading(false);
        }
      }
    } catch (err) { console.error('Path upload error:', err); setDocUploading(false); }
  };

  const handleUploadFromRecent = async (locationPath) => {
    setDocUploadResults(null);
    try {
      // Open native dialog pre-navigated to the recent location, add results to queue
      const browse = await window.zignagenixAPI.browseBiolabDocuments({ defaultPath: locationPath });
      if (browse && !browse.canceled && browse.previews?.length > 0) {
        const newPaths = browse.previews
          .filter(p => p.accessible && p.isSupported)
          .map(p => ({
            path: p.path,
            name: p.name,
            ext: p.ext,
            size: p.size,
            isNetwork: p.isNetwork,
            id: `${p.path}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
          }));
        if (newPaths.length > 0) setStagedPaths(prev => {
          const existing = new Set(prev.map(p => p.path));
          return [...prev, ...newPaths.filter(p => !existing.has(p.path))];
        });
      }
    } catch (err) { console.error('Recent location upload error:', err); }
  };

  const handleDocAnalysis = async () => {
    if (!selectedDocument) return;
    setDocAnalysisLoading(true);
    setDocAnalysisResult(null);
    try {
      // First attempt analysis
      let result = await window.zignagenixAPI.analyzeBiolabDocument({
        documentId: selectedDocument.id,
        analysisType: docAnalysisType,
        queryText: docAnalysisQuery || undefined,
        includeProtocolContext: true,
      });

      // If analysis fails due to missing/corrupted text, auto-re-extract and retry
      if (result?.error && result.message && (result.message.includes('no extracted text') || result.message.includes('corrupted or missing'))) {
        console.log('[BiolabPage] No extracted text — attempting auto re-extraction...');
        const reResult = await window.zignagenixAPI.reextractBiolabDocument(selectedDocument.id);
        if (reResult && !reResult.error && reResult.wordCount > 0) {
          // Re-extraction succeeded — retry analysis
          result = await window.zignagenixAPI.analyzeBiolabDocument({
            documentId: selectedDocument.id,
            analysisType: docAnalysisType,
            queryText: docAnalysisQuery || undefined,
            includeProtocolContext: true,
          });
          // Refresh the document list to reflect updated word counts
          loadDocuments();
        }
      }
      setDocAnalysisResult(result);
    } catch (err) { console.error('Doc analysis error:', err); }
    setDocAnalysisLoading(false);
  };

  const handleLoadDocAnalysisHistory = async (docId) => {
    try {
      const history = await window.zignagenixAPI.getBiolabDocAnalysisHistory({ documentId: docId });
      setDocAnalysisHistory(Array.isArray(history) ? history : []);
      setShowDocAnalysisHistory(true);
    } catch (err) { console.error('Analysis history error:', err); }
  };

  const handleDeactivateDocument = async (id) => {
    if (!window.confirm('Are you sure you want to deactivate this document?')) return;
    try {
      await window.zignagenixAPI.deactivateBiolabDocument(id);
      loadDocuments();
      if (selectedDocument?.id === id) setSelectedDocument(null);
    } catch (err) { console.error('Deactivate error:', err); }
  };

  const handleReextractDocument = async (id) => {
    try {
      const result = await window.zignagenixAPI.reextractBiolabDocument(id);
      if (result?.error) {
        alert('Re-extraction failed: ' + result.message);
      } else {
        alert(`Text re-extracted successfully (${result.wordCount || 0} words, ${result.pageCount || 0} pages)`);
        loadDocuments();
      }
    } catch (err) { console.error('Re-extract error:', err); }
  };

  const handleOpenDocument = async (id) => {
    try { await window.zignagenixAPI.openBiolabDocument(id); }
    catch (err) { console.error('Open document error:', err); }
  };

  const DOC_ANALYSIS_TYPES = [
    { id: 'cell_count_analysis', label: 'Cell Count Report' },
    { id: 'key_findings', label: 'Key Findings' },
    { id: 'literature_review', label: 'Literature Review' },
    { id: 'protocol_extraction', label: 'Protocol Extraction' },
    { id: 'data_interpretation', label: 'Data Interpretation' },
    { id: 'compliance_review', label: 'Compliance Review' },
    { id: 'safety_analysis', label: 'Safety Analysis' },
    { id: 'methodology_critique', label: 'Methodology Critique' },
    { id: 'comparison', label: 'Protocol Comparison' },
    { id: 'custom', label: 'Custom Query' },
  ];

  // ── Published Research Helper ─────────────────────────────────
  const loadPublishedResearch = async () => {
    try {
      const data = await window.zignagenixAPI.getPublishedResearch({});
      setPublishedResearch(Array.isArray(data) ? data : []);
    } catch (err) { console.error('Load published research error:', err); }
  };

  // Filter protocols
  const filteredProtocols = protocols.filter((p) => {
    const matchesCategory = categoryFilter === 'All' || p.category === categoryFilter;
    const matchesCellType = cellTypeFilter === 'All' || p.cellType === cellTypeFilter;
    const matchesSearch =
      !searchTerm ||
      p.protocolName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.protocolNumber?.toLowerCase().includes(searchTerm.toLowerCase());

    return matchesCategory && matchesCellType && matchesSearch;
  });

  // Filter published research
  const filteredPublishedResearch = publishedResearch.filter((r) => {
    const matchesCellType = researchCellTypeFilter === 'All' || r.cellType === researchCellTypeFilter;
    const matchesPhase = researchPhaseFilter === 'All' || r.studyPhase === researchPhaseFilter;
    const matchesSearch =
      !researchSearchTerm ||
      r.title?.toLowerCase().includes(researchSearchTerm.toLowerCase()) ||
      r.authors?.toLowerCase().includes(researchSearchTerm.toLowerCase());

    return matchesCellType && matchesPhase && matchesSearch;
  });

  return (
    <div style={{ background: COLORS.light, minHeight: '100vh', padding: 24 }}>
      {/* Header */}
      <div style={{ marginBottom: 32 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <div style={{ color: COLORS.primary }}>
              <Dna size={32} />
            </div>
            <div>
              <h1 style={{ margin: 0, fontSize: 28, fontWeight: 700, color: COLORS.dark }}>Biolab AI</h1>
              <p
                style={{
                  margin: '4px 0 0 0',
                  fontSize: 14,
                  color: COLORS.textLight,
                }}
              >
                Stem Cell Protocols & Comparative Analysis Database
              </p>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
            <PrintButton />
            <button
              onClick={loadData}
              disabled={loading}
              style={{
                padding: '10px 16px',
                boxShadow: '5px 5px 10px rgba(186,199,216,0.45), -5px -5px 10px rgba(255,255,255,0.85)',
                borderRadius: 6,
                background: '#F8F9FA',
                color: COLORS.primary,
                cursor: loading ? 'not-allowed' : 'pointer',
                fontSize: 14,
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                opacity: loading ? 0.5 : 1,
              }}
            >
              <RefreshCw size={16} />
              Refresh
            </button>
          </div>
        </div>
      </div>

      {/* KPI Row */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 32 }}>
        <KPICard
          label="Total Protocols"
          value={summary.totalProtocols || 0}
          icon={Dna}
          color={COLORS.primary}
        />
        <KPICard
          label="Comparative Analyses"
          value={summary.totalComparisons || 0}
          icon={Filter}
          color={COLORS.green}
        />
        <KPICard
          label="Patient Treatments"
          value={summary.totalTreatments || 0}
          icon={Users}
          color={COLORS.orange}
        />
        <KPICard
          label="Active Categories"
          value={Object.keys(summary.byCategory || {}).length}
          icon={ChevronDown}
          color={COLORS.red}
        />
      </div>

      {/* Tabs */}
      <div
        style={{
          background: '#F8F9FA',
          borderRadius: 12,
          boxShadow: '5px 5px 10px rgba(186,199,216,0.45), -5px -5px 10px rgba(255,255,255,0.85)',
          overflow: 'hidden',
        }}
      >
        {/* Tab Navigation — grouped dropdown menus (consolidated from 16 flat
            tabs into 6 functional menus; no more horizontal scroll). The
            active section + page (the selected tab key) are shown beside the menus. */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 4,
            borderBottom: `1px solid ${COLORS.border}`,
            padding: '6px 24px',
          }}
        >
          {TAB_GROUPS.map((g) => (
            <BiolabNavMenu key={g.label} group={g} activeTab={activeTab} onSelect={setActiveTab} />
          ))}
          <span style={{ marginLeft: 'auto', fontSize: 13, fontWeight: 600, color: COLORS.primary, whiteSpace: 'nowrap' }}>
            {TAB_LABELS[activeTab] || ''}
          </span>
        </div>

        {/* Tab Content */}
        <div style={{ padding: 24 }}>
          {/* Protocols Tab */}
          {activeTab === 'protocols' && (
            <div>
              {/* Filter Bar */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 16, marginBottom: 24 }}>
                <div>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: COLORS.dark, marginBottom: 6 }}>
                    Category
                  </label>
                  <select
                    value={categoryFilter}
                    onChange={(e) => setCategoryFilter(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      boxShadow: 'inset 2px 2px 4px rgba(186,199,216,0.45), inset -2px -2px 4px rgba(255,255,255,0.85)',
                      borderRadius: 6,
                      fontSize: 13,
                      fontFamily: 'inherit',
                      background: '#F8F9FA',
                      cursor: 'pointer',
                    }}
                  >
                    <option value="All">All Categories</option>
                    {Object.keys(CATEGORY_BADGE_COLORS).map((cat) => (
                      <option key={cat} value={cat}>
                        {cat}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: COLORS.dark, marginBottom: 6 }}>
                    Cell Type
                  </label>
                  <select
                    value={cellTypeFilter}
                    onChange={(e) => setCellTypeFilter(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      boxShadow: 'inset 2px 2px 4px rgba(186,199,216,0.45), inset -2px -2px 4px rgba(255,255,255,0.85)',
                      borderRadius: 6,
                      fontSize: 13,
                      fontFamily: 'inherit',
                      background: '#F8F9FA',
                      cursor: 'pointer',
                    }}
                  >
                    <option value="All">All Cell Types</option>
                    {CELL_TYPE_OPTIONS.map((ct) => (
                      <option key={ct} value={ct}>
                        {ct}
                      </option>
                    ))}
                  </select>
                </div>

                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: COLORS.dark, marginBottom: 6 }}>
                    Search
                  </label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Search size={16} style={{ color: COLORS.textLight }} />
                    <input
                      type="text"
                      placeholder="Search protocol name or number..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      style={{
                        flex: 1,
                        padding: '10px 12px',
                        boxShadow: 'inset 2px 2px 4px rgba(186,199,216,0.45), inset -2px -2px 4px rgba(255,255,255,0.85)',
                        borderRadius: 6,
                        fontSize: 13,
                        fontFamily: 'inherit',
                      }}
                    />
                  </div>
                </div>
              </div>

              <div style={{ marginBottom: 24 }}>
                <button
                  onClick={() => setIsNewProtocolOpen(true)}
                  style={{
                    padding: '10px 24px',
                    border: 'none',
                    borderRadius: 6,
                    background: COLORS.primary,
                    color: COLORS.white,
                    cursor: 'pointer',
                    fontSize: 14,
                    fontWeight: 600,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                  }}
                >
                  <Plus size={16} />
                  New Protocol
                </button>
              </div>

              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: `1px solid ${COLORS.border}` }}>
                      <th style={{ padding: 12, textAlign: 'left', fontSize: 12, fontWeight: 600, color: COLORS.dark }}>
                        Protocol Name
                      </th>
                      <th style={{ padding: 12, textAlign: 'left', fontSize: 12, fontWeight: 600, color: COLORS.dark }}>
                        Number
                      </th>
                      <th style={{ padding: 12, textAlign: 'left', fontSize: 12, fontWeight: 600, color: COLORS.dark }}>
                        Cell Type
                      </th>
                      <th style={{ padding: 12, textAlign: 'left', fontSize: 12, fontWeight: 600, color: COLORS.dark }}>
                        Category
                      </th>
                      <th style={{ padding: 12, textAlign: 'left', fontSize: 12, fontWeight: 600, color: COLORS.dark }}>
                        Status
                      </th>
                      <th style={{ padding: 12, textAlign: 'left', fontSize: 12, fontWeight: 600, color: COLORS.dark }}>
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {!Array.isArray(filteredProtocols) || filteredProtocols.length === 0 ? (
                      <tr>
                        <td colSpan="6" style={{ padding: 24, textAlign: 'center', color: COLORS.textLight }}>
                          No protocols found
                        </td>
                      </tr>
                    ) : (
                      filteredProtocols.map((protocol) => {
                        const categoryColor = CATEGORY_BADGE_COLORS[protocol.category] || {
                          bg: '#f1f5f9',
                          text: '#475569',
                        };
                        return (
                          <tr key={protocol.id} style={{ borderBottom: `1px solid ${COLORS.border}` }}>
                            <td style={{ padding: 12, fontSize: 13, color: COLORS.text }}>{protocol.protocolName}</td>
                            <td style={{ padding: 12, fontSize: 13, color: COLORS.text }}>{protocol.protocolNumber}</td>
                            <td style={{ padding: 12, fontSize: 13, color: COLORS.text }}>{protocol.cellType}</td>
                            <td style={{ padding: 12 }}>
                              <Badge label={protocol.category} bgColor={categoryColor.bg} textColor={categoryColor.text} />
                            </td>
                            <td style={{ padding: 12 }}>
                              <Badge label={protocol.regulatoryStatus} bgColor="#dbeafe" textColor="#1d4ed8" />
                            </td>
                            <td style={{ padding: 12 }}>
                              <button
                                onClick={() => setExpandedProtocol(protocol)}
                                style={{
                                  background: 'none',
                                  border: 'none',
                                  cursor: 'pointer',
                                  color: COLORS.primary,
                                  padding: 4,
                                }}
                              >
                                <Eye size={16} />
                              </button>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Comparative Analysis Tab */}
          {activeTab === 'comparisons' && (
            <div>
              <div style={{ marginBottom: 24 }}>
                <button
                  onClick={() => setIsComparisonOpen(true)}
                  style={{
                    padding: '10px 24px',
                    border: 'none',
                    borderRadius: 6,
                    background: COLORS.primary,
                    color: COLORS.white,
                    cursor: 'pointer',
                    fontSize: 14,
                    fontWeight: 600,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                  }}
                >
                  <Plus size={16} />
                  Add Comparison
                </button>
              </div>

              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: `1px solid ${COLORS.border}` }}>
                      <th style={{ padding: 12, textAlign: 'left', fontSize: 12, fontWeight: 600, color: COLORS.dark }}>
                        Lab Name
                      </th>
                      <th style={{ padding: 12, textAlign: 'left', fontSize: 12, fontWeight: 600, color: COLORS.dark }}>
                        Location
                      </th>
                      <th style={{ padding: 12, textAlign: 'left', fontSize: 12, fontWeight: 600, color: COLORS.dark }}>
                        Cell Type
                      </th>
                      <th style={{ padding: 12, textAlign: 'left', fontSize: 12, fontWeight: 600, color: COLORS.dark }}>
                        Efficacy Score
                      </th>
                      <th style={{ padding: 12, textAlign: 'left', fontSize: 12, fontWeight: 600, color: COLORS.dark }}>
                        Safety Score
                      </th>
                      <th style={{ padding: 12, textAlign: 'left', fontSize: 12, fontWeight: 600, color: COLORS.dark }}>
                        Viability %
                      </th>
                      <th style={{ padding: 12, textAlign: 'left', fontSize: 12, fontWeight: 600, color: COLORS.dark }}>
                        Sample Size
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {!Array.isArray(comparisons) || comparisons.length === 0 ? (
                      <tr>
                        <td colSpan="7" style={{ padding: 24, textAlign: 'center', color: COLORS.textLight }}>
                          No comparative analyses yet
                        </td>
                      </tr>
                    ) : (
                      comparisons.map((comp) => (
                        <tr key={comp.id} style={{ borderBottom: `1px solid ${COLORS.border}` }}>
                          <td style={{ padding: 12, fontSize: 13, color: COLORS.text }}>{comp.labName}</td>
                          <td style={{ padding: 12, fontSize: 13, color: COLORS.text }}>{comp.labLocation || 'N/A'}</td>
                          <td style={{ padding: 12, fontSize: 13, color: COLORS.text }}>{comp.cellType}</td>
                          <td style={{ padding: 12 }}>
                            <ScoreBar score={comp.efficacyScore} />
                          </td>
                          <td style={{ padding: 12 }}>
                            <ScoreBar score={comp.safetyScore} />
                          </td>
                          <td style={{ padding: 12, fontSize: 13, color: COLORS.text }}>
                            {comp.viabilityPercent || 'N/A'}%
                          </td>
                          <td style={{ padding: 12, fontSize: 13, color: COLORS.text }}>
                            {comp.sampleSize || 'N/A'}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Patient Treatments Tab */}
          {activeTab === 'treatments' && (
            <div>
              <div style={{ marginBottom: 24 }}>
                <button
                  onClick={() => setIsTreatmentOpen(true)}
                  style={{
                    padding: '10px 24px',
                    border: 'none',
                    borderRadius: 6,
                    background: COLORS.primary,
                    color: COLORS.white,
                    cursor: 'pointer',
                    fontSize: 14,
                    fontWeight: 600,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                  }}
                >
                  <Plus size={16} />
                  Log Treatment
                </button>
              </div>

              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: `1px solid ${COLORS.border}` }}>
                      <th style={{ padding: 12, textAlign: 'left', fontSize: 12, fontWeight: 600, color: COLORS.dark }}>
                        Patient
                      </th>
                      <th style={{ padding: 12, textAlign: 'left', fontSize: 12, fontWeight: 600, color: COLORS.dark }}>
                        Protocol
                      </th>
                      <th style={{ padding: 12, textAlign: 'left', fontSize: 12, fontWeight: 600, color: COLORS.dark }}>
                        Treatment Date
                      </th>
                      <th style={{ padding: 12, textAlign: 'left', fontSize: 12, fontWeight: 600, color: COLORS.dark }}>
                        Dose
                      </th>
                      <th style={{ padding: 12, textAlign: 'left', fontSize: 12, fontWeight: 600, color: COLORS.dark }}>
                        Route
                      </th>
                      <th style={{ padding: 12, textAlign: 'left', fontSize: 12, fontWeight: 600, color: COLORS.dark }}>
                        Outcome
                      </th>
                      <th style={{ padding: 12, textAlign: 'left', fontSize: 12, fontWeight: 600, color: COLORS.dark }}>
                        Follow-up
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {!Array.isArray(treatments) || treatments.length === 0 ? (
                      <tr>
                        <td colSpan="7" style={{ padding: 24, textAlign: 'center', color: COLORS.textLight }}>
                          No treatments logged yet
                        </td>
                      </tr>
                    ) : (
                      treatments.map((tx) => {
                        const outcomeColors = OUTCOME_COLORS[tx.outcome] || OUTCOME_COLORS.Pending;
                        return (
                          <tr key={tx.id} style={{ borderBottom: `1px solid ${COLORS.border}` }}>
                            <td style={{ padding: 12, fontSize: 13, color: COLORS.text }}>{tx.patientId}</td>
                            <td style={{ padding: 12, fontSize: 13, color: COLORS.text }}>{tx.protocolId}</td>
                            <td style={{ padding: 12, fontSize: 13, color: COLORS.text }}>
                              {tx.treatmentDate ? new Date(tx.treatmentDate).toLocaleDateString() : 'N/A'}
                            </td>
                            <td style={{ padding: 12, fontSize: 13, color: COLORS.text }}>{tx.dose || 'N/A'}</td>
                            <td style={{ padding: 12, fontSize: 13, color: COLORS.text }}>{tx.route || 'N/A'}</td>
                            <td style={{ padding: 12 }}>
                              <Badge label={tx.outcome} bgColor={outcomeColors.bg} textColor={outcomeColors.text} />
                            </td>
                            <td style={{ padding: 12, fontSize: 13, color: COLORS.text }}>
                              {tx.followUpDate ? new Date(tx.followUpDate).toLocaleDateString() : 'N/A'}
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Published Research Tab */}
          {activeTab === 'published' && (
            <div>
              {/* KPI Cards */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 24 }}>
                <KPICard
                  label="Total Research"
                  value={publishedResearch.length}
                  icon={BookOpen}
                  color={COLORS.primary}
                />
                <KPICard
                  label="Avg Efficacy"
                  value={
                    publishedResearch.length > 0
                      ? (
                          publishedResearch.reduce((sum, r) => sum + (r.efficacyRating || 0), 0) /
                          publishedResearch.length
                        ).toFixed(1)
                      : 0
                  }
                  icon={CheckCircle}
                  color={COLORS.green}
                />
                <KPICard
                  label="Phase III+ Studies"
                  value={publishedResearch.filter((r) => r.studyPhase && ['Phase III', 'Phase IV'].includes(r.studyPhase)).length}
                  icon={Zap}
                  color={COLORS.orange}
                />
                <KPICard
                  label="Clinical Trials"
                  value={publishedResearch.filter((r) => r.nctNumber).length}
                  icon={Users}
                  color={COLORS.red}
                />
              </div>

              {/* Filter Bar */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16, marginBottom: 24 }}>
                <div>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: COLORS.dark, marginBottom: 6 }}>
                    Cell Type
                  </label>
                  <select
                    value={researchCellTypeFilter}
                    onChange={(e) => setResearchCellTypeFilter(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      boxShadow: 'inset 2px 2px 4px rgba(186,199,216,0.45), inset -2px -2px 4px rgba(255,255,255,0.85)',
                      borderRadius: 6,
                      fontSize: 13,
                      fontFamily: 'inherit',
                      background: '#F8F9FA',
                      cursor: 'pointer',
                    }}
                  >
                    <option value="All">All Cell Types</option>
                    {CELL_TYPE_OPTIONS.map((ct) => (
                      <option key={ct} value={ct}>
                        {ct}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: COLORS.dark, marginBottom: 6 }}>
                    Study Phase
                  </label>
                  <select
                    value={researchPhaseFilter}
                    onChange={(e) => setResearchPhaseFilter(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      boxShadow: 'inset 2px 2px 4px rgba(186,199,216,0.45), inset -2px -2px 4px rgba(255,255,255,0.85)',
                      borderRadius: 6,
                      fontSize: 13,
                      fontFamily: 'inherit',
                      background: '#F8F9FA',
                      cursor: 'pointer',
                    }}
                  >
                    <option value="All">All Phases</option>
                    {STUDY_PHASE_OPTIONS.map((phase) => (
                      <option key={phase} value={phase}>
                        {phase}
                      </option>
                    ))}
                  </select>
                </div>

                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: COLORS.dark, marginBottom: 6 }}>
                    Search
                  </label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Search size={16} style={{ color: COLORS.textLight }} />
                    <input
                      type="text"
                      placeholder="Search by title or authors..."
                      value={researchSearchTerm}
                      onChange={(e) => setResearchSearchTerm(e.target.value)}
                      style={{
                        flex: 1,
                        padding: '10px 12px',
                        boxShadow: 'inset 2px 2px 4px rgba(186,199,216,0.45), inset -2px -2px 4px rgba(255,255,255,0.85)',
                        borderRadius: 6,
                        fontSize: 13,
                        fontFamily: 'inherit',
                      }}
                    />
                  </div>
                </div>
              </div>

              <div style={{ marginBottom: 24 }}>
                <button
                  onClick={() => setIsPublishedResearchOpen(true)}
                  style={{
                    padding: '10px 24px',
                    border: 'none',
                    borderRadius: 6,
                    background: COLORS.primary,
                    color: COLORS.white,
                    cursor: 'pointer',
                    fontSize: 14,
                    fontWeight: 600,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                  }}
                >
                  <Plus size={16} />
                  Add Research
                </button>
              </div>

              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: `1px solid ${COLORS.border}` }}>
                      <th style={{ padding: 12, textAlign: 'left', fontSize: 12, fontWeight: 600, color: COLORS.dark }}>
                        Title
                      </th>
                      <th style={{ padding: 12, textAlign: 'left', fontSize: 12, fontWeight: 600, color: COLORS.dark }}>
                        Cell Type
                      </th>
                      <th style={{ padding: 12, textAlign: 'left', fontSize: 12, fontWeight: 600, color: COLORS.dark }}>
                        Condition
                      </th>
                      <th style={{ padding: 12, textAlign: 'left', fontSize: 12, fontWeight: 600, color: COLORS.dark }}>
                        Phase
                      </th>
                      <th style={{ padding: 12, textAlign: 'left', fontSize: 12, fontWeight: 600, color: COLORS.dark }}>
                        Efficacy
                      </th>
                      <th style={{ padding: 12, textAlign: 'left', fontSize: 12, fontWeight: 600, color: COLORS.dark }}>
                        Safety
                      </th>
                      <th style={{ padding: 12, textAlign: 'left', fontSize: 12, fontWeight: 600, color: COLORS.dark }}>
                        Year
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {!Array.isArray(filteredPublishedResearch) || filteredPublishedResearch.length === 0 ? (
                      <tr>
                        <td colSpan="7" style={{ padding: 24, textAlign: 'center', color: COLORS.textLight }}>
                          No published research found
                        </td>
                      </tr>
                    ) : (
                      filteredPublishedResearch.map((research) => (
                        <tr key={research.id} style={{ borderBottom: `1px solid ${COLORS.border}` }}>
                          <td style={{ padding: 12, fontSize: 13, color: COLORS.text, maxWidth: 300 }}>
                            <div style={{ fontWeight: 600 }}>{research.title}</div>
                            <div style={{ fontSize: 11, color: COLORS.textLight, marginTop: 2 }}>
                              {research.authors}
                            </div>
                          </td>
                          <td style={{ padding: 12, fontSize: 13, color: COLORS.text }}>{research.cellType}</td>
                          <td style={{ padding: 12, fontSize: 13, color: COLORS.text }}>{research.conditionTreated || 'N/A'}</td>
                          <td style={{ padding: 12, fontSize: 13, color: COLORS.text }}>{research.studyPhase}</td>
                          <td style={{ padding: 12 }}>
                            <ScoreBar score={research.efficacyRating || 5} />
                          </td>
                          <td style={{ padding: 12 }}>
                            <ScoreBar score={research.safetyRating || 5} />
                          </td>
                          <td style={{ padding: 12, fontSize: 13, color: COLORS.text }}>
                            {research.publicationDate ? new Date(research.publicationDate).getFullYear() : 'N/A'}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* AI Analysis Tab */}
          {activeTab === 'analysis' && (
            <div>
              {/* Analysis Query Section */}
              <div style={{ background: COLORS.light, padding: 24, borderRadius: 12, marginBottom: 24 }}>
                <h3 style={{ fontSize: 16, fontWeight: 600, color: COLORS.dark, marginBottom: 16 }}>
                  Claude AI Protocol Analysis Engine
                </h3>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
                  <div>
                    <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: COLORS.dark, marginBottom: 6 }}>
                      Analysis Type
                    </label>
                    <select
                      value={analysisType}
                      onChange={(e) => setAnalysisType(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        boxShadow: 'inset 2px 2px 4px rgba(186,199,216,0.45), inset -2px -2px 4px rgba(255,255,255,0.85)',
                        borderRadius: 6,
                        fontSize: 13,
                        fontFamily: 'inherit',
                        background: '#F8F9FA',
                        cursor: 'pointer',
                      }}
                    >
                      {AI_ANALYSIS_TYPES.map((type) => (
                        <option key={type} value={type}>
                          {type.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: COLORS.dark, marginBottom: 6 }}>
                      Condition Filter (Optional)
                    </label>
                    <input
                      type="text"
                      value={conditionFilter}
                      onChange={(e) => setConditionFilter(e.target.value)}
                      placeholder="e.g., Diabetes, Cancer"
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        boxShadow: 'inset 2px 2px 4px rgba(186,199,216,0.45), inset -2px -2px 4px rgba(255,255,255,0.85)',
                        borderRadius: 6,
                        fontSize: 13,
                        fontFamily: 'inherit',
                      }}
                    />
                  </div>
                </div>

                <div style={{ marginBottom: 16 }}>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: COLORS.dark, marginBottom: 6 }}>
                    Query or Question
                  </label>
                  <textarea
                    value={queryText}
                    onChange={(e) => setQueryText(e.target.value)}
                    placeholder="Ask a question about your protocols, treatments, or research..."
                    rows={4}
                    style={{
                      width: '100%',
                      padding: 12,
                      boxShadow: 'inset 2px 2px 4px rgba(186,199,216,0.45), inset -2px -2px 4px rgba(255,255,255,0.85)',
                      borderRadius: 6,
                      fontSize: 13,
                      fontFamily: 'inherit',
                      resize: 'vertical',
                    }}
                  />
                </div>

                <button
                  onClick={handleRunAIAnalysis}
                  disabled={analysisLoading}
                  style={{
                    padding: '12px 24px',
                    border: 'none',
                    borderRadius: 6,
                    background: COLORS.primary,
                    color: COLORS.white,
                    cursor: analysisLoading ? 'not-allowed' : 'pointer',
                    fontSize: 14,
                    fontWeight: 600,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    opacity: analysisLoading ? 0.5 : 1,
                  }}
                >
                  <Zap size={16} />
                  {analysisLoading ? 'Running Analysis...' : 'Run Analysis'}
                </button>
              </div>

              {/* Analysis Results */}
              {analysisResult && (
                <div style={{ background: '#F8F9FA', boxShadow: '5px 5px 10px rgba(186,199,216,0.45), -5px -5px 10px rgba(255,255,255,0.85)', borderRadius: 12, padding: 24, marginBottom: 24 }}>
                  <h3 style={{ fontSize: 16, fontWeight: 600, color: COLORS.dark, marginBottom: 12 }}>
                    Analysis Result
                  </h3>
                  <div
                    style={{
                      fontSize: 13,
                      color: COLORS.text,
                      lineHeight: 1.8,
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                    }}
                  >
                    {analysisResult.response
                      || (analysisResult.error
                            ? `⚠ ${analysisResult.message || 'AI analysis failed'}`
                            : 'No response generated')}
                  </div>
                  {analysisResult.dataPointsUsed && (
                    <div
                      style={{
                        marginTop: 16,
                        fontSize: 12,
                        color: COLORS.textLight,
                        paddingTop: 16,
                        borderTop: `1px solid ${COLORS.border}`,
                      }}
                    >
                      Data Points Used: {analysisResult.dataPointsUsed} | AI-Powered: {analysisResult.isAI ? 'Yes' : 'No'}
                    </div>
                  )}
                </div>
              )}

              {/* Analysis History */}
              <div>
                <button
                  onClick={() => setShowAnalysisHistory(!showAnalysisHistory)}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: 0,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    fontSize: 14,
                    fontWeight: 600,
                    color: COLORS.primary,
                    marginBottom: 16,
                  }}
                >
                  {showAnalysisHistory ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                  Analysis History ({aiAnalyses.length})
                </button>

                {showAnalysisHistory && (
                  <div style={{ display: 'grid', gap: 12 }}>
                    {aiAnalyses.length === 0 ? (
                      <div style={{ color: COLORS.textLight, fontSize: 13, padding: 12 }}>
                        No analysis history yet
                      </div>
                    ) : (
                      aiAnalyses.map((analysis, idx) => (
                        <div
                          key={idx}
                          style={{
                            background: COLORS.light,
                            boxShadow: '5px 5px 10px rgba(186,199,216,0.45), -5px -5px 10px rgba(255,255,255,0.85)',
                            borderRadius: 8,
                            padding: 12,
                            fontSize: 13,
                          }}
                        >
                          <div style={{ fontWeight: 600, color: COLORS.dark, marginBottom: 4 }}>
                            {analysis.analysisType?.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
                          </div>
                          <div style={{ color: COLORS.textLight, fontSize: 12 }}>
                            {analysis.queryText?.substring(0, 100)}
                            {analysis.queryText?.length > 100 ? '...' : ''}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Intelligence Dashboard Tab */}
          {activeTab === 'intelligence' && (
            <div>
              {/* Large KPI Row */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 32 }}>
                <KPICard
                  label="Published Research"
                  value={intelligenceSummary.publishedResearch || 0}
                  icon={BookOpen}
                  color={COLORS.primary}
                />
                <KPICard
                  label="Active Protocols"
                  value={intelligenceSummary.totalProtocols || 0}
                  icon={Dna}
                  color={COLORS.green}
                />
                <KPICard
                  label="Total Treatments"
                  value={intelligenceSummary.totalTreatments || 0}
                  icon={Users}
                  color={COLORS.orange}
                />
                <KPICard
                  label="AI Analyses"
                  value={intelligenceSummary.aiAnalyses || 0}
                  icon={Zap}
                  color={COLORS.red}
                />
              </div>

              {/* Two Column Layout */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24, marginBottom: 24 }}>
                {/* Left Column: Top Conditions & Cell Types */}
                <div>
                  <h3 style={{ fontSize: 14, fontWeight: 600, color: COLORS.dark, marginBottom: 16 }}>
                    Top Conditions
                  </h3>
                  <div style={{ marginBottom: 32 }}>
                    {intelligenceSummary.topConditions && intelligenceSummary.topConditions.length > 0 ? (
                      intelligenceSummary.topConditions.map((condition, idx) => (
                        <div key={idx} style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 12 }}>
                          <div
                            style={{
                              flex: 1,
                              height: 24,
                              background: COLORS.light,
                              borderRadius: 4,
                              position: 'relative',
                              overflow: 'hidden',
                            }}
                          >
                            <div
                              style={{
                                height: '100%',
                                width: `${(condition.count / (intelligenceSummary.topConditions[0]?.count || 1)) * 100}%`,
                                background: COLORS.primary,
                                borderRadius: 4,
                              }}
                            />
                          </div>
                          <div style={{ minWidth: 120, fontSize: 12 }}>
                            <strong>{condition.name}</strong>
                          </div>
                          <div style={{ minWidth: 40, fontSize: 12, color: COLORS.textLight, textAlign: 'right' }}>
                            {condition.count}
                          </div>
                        </div>
                      ))
                    ) : (
                      <div style={{ color: COLORS.textLight, fontSize: 13 }}>No condition data available</div>
                    )}
                  </div>

                  <h3 style={{ fontSize: 14, fontWeight: 600, color: COLORS.dark, marginBottom: 16 }}>
                    Top Cell Types
                  </h3>
                  <div>
                    {intelligenceSummary.topCellTypes && intelligenceSummary.topCellTypes.length > 0 ? (
                      intelligenceSummary.topCellTypes.map((cellType, idx) => (
                        <div key={idx} style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 12 }}>
                          <div
                            style={{
                              flex: 1,
                              height: 24,
                              background: COLORS.light,
                              borderRadius: 4,
                              position: 'relative',
                              overflow: 'hidden',
                            }}
                          >
                            <div
                              style={{
                                height: '100%',
                                width: `${(cellType.count / (intelligenceSummary.topCellTypes[0]?.count || 1)) * 100}%`,
                                background: COLORS.green,
                                borderRadius: 4,
                              }}
                            />
                          </div>
                          <div style={{ minWidth: 100, fontSize: 12 }}>
                            <strong>{cellType.name}</strong>
                          </div>
                          <div style={{ minWidth: 40, fontSize: 12, color: COLORS.textLight, textAlign: 'right' }}>
                            {cellType.count}
                          </div>
                        </div>
                      ))
                    ) : (
                      <div style={{ color: COLORS.textLight, fontSize: 13 }}>No cell type data available</div>
                    )}
                  </div>
                </div>

                {/* Right Column: Recent Analyses & Protocol Scores */}
                <div>
                  <h3 style={{ fontSize: 14, fontWeight: 600, color: COLORS.dark, marginBottom: 16 }}>
                    Recent AI Analyses
                  </h3>
                  <div style={{ marginBottom: 32 }}>
                    {intelligenceSummary.recentAnalyses && intelligenceSummary.recentAnalyses.length > 0 ? (
                      intelligenceSummary.recentAnalyses.map((analysis, idx) => (
                        <div
                          key={idx}
                          style={{
                            background: COLORS.light,
                            boxShadow: '5px 5px 10px rgba(186,199,216,0.45), -5px -5px 10px rgba(255,255,255,0.85)',
                            borderRadius: 8,
                            padding: 12,
                            marginBottom: 10,
                            fontSize: 12,
                          }}
                        >
                          <div style={{ fontWeight: 600, color: COLORS.dark }}>
                            {analysis.type?.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
                          </div>
                          <div style={{ color: COLORS.textLight, fontSize: 11, marginTop: 4 }}>
                            {new Date(analysis.timestamp).toLocaleDateString()}
                          </div>
                        </div>
                      ))
                    ) : (
                      <div style={{ color: COLORS.textLight, fontSize: 13 }}>No recent analyses</div>
                    )}
                  </div>

                  <h3 style={{ fontSize: 14, fontWeight: 600, color: COLORS.dark, marginBottom: 16 }}>
                    Protocol Scores Overview
                  </h3>
                  <div style={{ marginBottom: 16 }}>
                    <button
                      onClick={handleRecalculateAllScores}
                      disabled={loading}
                      style={{
                        padding: '10px 16px',
                        border: 'none',
                        borderRadius: 6,
                        background: COLORS.primary,
                        color: COLORS.white,
                        cursor: loading ? 'not-allowed' : 'pointer',
                        fontSize: 13,
                        fontWeight: 600,
                        opacity: loading ? 0.5 : 1,
                      }}
                    >
                      {loading ? 'Recalculating...' : 'Recalculate All Scores'}
                    </button>
                  </div>
                  <div style={{ color: COLORS.textLight, fontSize: 12 }}>
                    Last updated: {new Date().toLocaleDateString()}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Research Workspace Tab — Phase F.3 */}
          {activeTab === 'research_workspace' && (
            <div style={{ margin: -24 }}>
              <ResearchTab />
            </div>
          )}

          {/* Proprietary Vault Tab — Phase F.3.4 */}
          {activeTab === 'vault' && (
            <div style={{ margin: -24 }}>
              <ProprietaryVaultView user={user} />
            </div>
          )}

          {/* Studies & Cohorts Tab — merged from ResearchStudiesPage */}
          {activeTab === 'studies' && (
            <div style={{ margin: -24 }}>
              <ResearchStudiesPage user={user} />
            </div>
          )}

          {/* Evidence & Protocols Tab — merged from ResearchDatabasePage */}
          {activeTab === 'evidence' && (
            <div style={{ margin: -24 }}>
              <ResearchDatabasePage user={user} />
            </div>
          )}

          {/* R6 Bio-Geometric Tab (research-side intelligence engine v1.1) */}
          {activeTab === 'r6_biogeo' && (
            <div style={{ margin: -24 }}>
              <R6BiogeoTab />
            </div>
          )}

          {/* Research Hypotheses registry — Migration 082 + IPC handlers in routes/research_engines.js.
              Lifecycle-managed L1/L2/L3 hypothesis storage with the anal SCC L3 entry as
              the first seeded record. */}
          {activeTab === 'hypotheses' && (
            <div style={{ padding: 4 }}>
              <HypothesesTab user={user} />
            </div>
          )}

          {/* Data Ingestion Tab */}
          {activeTab === 'ingestion' && (
            <div style={{ padding: 24 }}>
              <h3 style={{ margin: '0 0 20px', color: COLORS.dark, fontSize: 18 }}>
                External Data Ingestion
              </h3>
              <p style={{ color: COLORS.textLight, marginBottom: 20, fontSize: 14 }}>
                Search and import from PubMed (NCBI E-utilities) and ClinicalTrials.gov APIs
              </p>

              {/* Search controls */}
              <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
                <select
                  value={ingestionSource}
                  onChange={(e) => setIngestionSource(e.target.value)}
                  style={{ padding: '10px 14px', borderRadius: 6, boxShadow: 'inset 2px 2px 4px rgba(186,199,216,0.45), inset -2px -2px 4px rgba(255,255,255,0.85)', fontSize: 14, minWidth: 180 }}
                >
                  <option value="PubMed">PubMed</option>
                  <option value="ClinicalTrials.gov">ClinicalTrials.gov</option>
                </select>
                <input
                  type="text"
                  value={ingestionQuery}
                  onChange={(e) => setIngestionQuery(e.target.value)}
                  placeholder="e.g. mesenchymal stem cell osteoarthritis"
                  style={{ padding: '10px 14px', borderRadius: 6, boxShadow: 'inset 2px 2px 4px rgba(186,199,216,0.45), inset -2px -2px 4px rgba(255,255,255,0.85)', fontSize: 14, flex: 1, minWidth: 250 }}
                  onKeyDown={(e) => e.key === 'Enter' && handleIngestionSearch()}
                />
                <button
                  onClick={handleIngestionSearch}
                  disabled={ingestionLoading || !ingestionQuery.trim()}
                  style={{
                    padding: '10px 24px', borderRadius: 6, border: 'none',
                    background: ingestionLoading ? COLORS.textLight : COLORS.primary,
                    color: COLORS.white, fontSize: 14, fontWeight: 600, cursor: 'pointer',
                  }}
                >
                  {ingestionLoading ? 'Searching...' : 'Search'}
                </button>
              </div>

              {/* Results */}
              {ingestionResults.length > 0 && (
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                    <span style={{ fontWeight: 600, color: COLORS.dark }}>{ingestionResults.length} results found</span>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button
                        onClick={() => {
                          if (selectedImports.size === ingestionResults.length) setSelectedImports(new Set());
                          else setSelectedImports(new Set(ingestionResults.map((_, i) => i)));
                        }}
                        style={{ padding: '6px 12px', borderRadius: 6, boxShadow: '5px 5px 10px rgba(186,199,216,0.45), -5px -5px 10px rgba(255,255,255,0.85)', background: '#F8F9FA', cursor: 'pointer', fontSize: 13 }}
                      >
                        {selectedImports.size === ingestionResults.length ? 'Deselect All' : 'Select All'}
                      </button>
                      <button
                        onClick={handleIngestionImport}
                        disabled={selectedImports.size === 0 || ingestionLoading}
                        style={{
                          padding: '6px 16px', borderRadius: 6, border: 'none',
                          background: selectedImports.size > 0 ? COLORS.green : COLORS.textLight,
                          color: COLORS.white, cursor: 'pointer', fontSize: 13, fontWeight: 600,
                        }}
                      >
                        Import Selected ({selectedImports.size})
                      </button>
                    </div>
                  </div>
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                      <thead>
                        <tr style={{ background: COLORS.light }}>
                          <th style={{ padding: '10px 12px', textAlign: 'left', width: 40 }}></th>
                          <th style={{ padding: '10px 12px', textAlign: 'left' }}>Title</th>
                          <th style={{ padding: '10px 12px', textAlign: 'left' }}>Source</th>
                          <th style={{ padding: '10px 12px', textAlign: 'left' }}>ID</th>
                          <th style={{ padding: '10px 12px', textAlign: 'left' }}>Condition</th>
                        </tr>
                      </thead>
                      <tbody>
                        {ingestionResults.map((r, idx) => (
                          <tr key={idx} style={{ borderBottom: `1px solid ${COLORS.border}` }}>
                            <td style={{ padding: '10px 12px' }}>
                              <input
                                type="checkbox"
                                checked={selectedImports.has(idx)}
                                onChange={(e) => {
                                  const next = new Set(selectedImports);
                                  e.target.checked ? next.add(idx) : next.delete(idx);
                                  setSelectedImports(next);
                                }}
                              />
                            </td>
                            <td style={{ padding: '10px 12px', maxWidth: 300 }}>{r.title}</td>
                            <td style={{ padding: '10px 12px' }}>{r.source}</td>
                            <td style={{ padding: '10px 12px', fontFamily: 'monospace', fontSize: 12 }}>{r.pmid || r.nctNumber || '—'}</td>
                            <td style={{ padding: '10px 12px' }}>{r.conditionTreated || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Ingestion History */}
              <div style={{ marginTop: 32 }}>
                <h4 style={{ color: COLORS.dark, marginBottom: 12 }}>Import History</h4>
                <button onClick={loadIngestionHistory} style={{ padding: '6px 12px', borderRadius: 6, boxShadow: '5px 5px 10px rgba(186,199,216,0.45), -5px -5px 10px rgba(255,255,255,0.85)', background: '#F8F9FA', cursor: 'pointer', fontSize: 13, marginBottom: 12 }}>
                  Refresh History
                </button>
                {ingestionHistory.length > 0 && (
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <thead>
                      <tr style={{ background: COLORS.light }}>
                        <th style={{ padding: '8px 12px', textAlign: 'left' }}>Source</th>
                        <th style={{ padding: '8px 12px', textAlign: 'left' }}>Status</th>
                        <th style={{ padding: '8px 12px', textAlign: 'left' }}>Found</th>
                        <th style={{ padding: '8px 12px', textAlign: 'left' }}>Imported</th>
                        <th style={{ padding: '8px 12px', textAlign: 'left' }}>Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ingestionHistory.map((j) => (
                        <tr key={j.id} style={{ borderBottom: `1px solid ${COLORS.border}` }}>
                          <td style={{ padding: '8px 12px' }}>{j.source}</td>
                          <td style={{ padding: '8px 12px' }}>
                            <Badge
                              label={j.status}
                              bgColor={j.status === 'completed' ? '#dcfce7' : j.status === 'failed' ? '#fef2f2' : '#f1f5f9'}
                              textColor={j.status === 'completed' ? '#15803d' : j.status === 'failed' ? '#DC3545' : '#475569'}
                            />
                          </td>
                          <td style={{ padding: '8px 12px' }}>{j.records_found || 0}</td>
                          <td style={{ padding: '8px 12px' }}>{j.records_imported || 0}</td>
                          <td style={{ padding: '8px 12px' }}>{j.created_at ? new Date(j.created_at).toLocaleDateString() : '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          )}

          {/* Doctor Dashboard Tab */}
          {activeTab === 'doctor' && (
            <div style={{ padding: 24 }}>
              <h3 style={{ margin: '0 0 20px', color: COLORS.dark, fontSize: 18 }}>
                Doctor Protocol Dashboard
              </h3>

              {/* Side-by-Side Protocol Comparison */}
              <div style={{ background: COLORS.light, borderRadius: 12, padding: 20, marginBottom: 24 }}>
                <h4 style={{ margin: '0 0 16px', color: COLORS.dark }}>Protocol Comparison Engine</h4>
                <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                  <div style={{ flex: 1, minWidth: 200 }}>
                    <label style={{ fontSize: 12, color: COLORS.textLight, display: 'block', marginBottom: 4 }}>Protocol A</label>
                    <select
                      value={selectedProtocolA}
                      onChange={(e) => setSelectedProtocolA(e.target.value)}
                      style={{ width: '100%', padding: '10px 14px', borderRadius: 6, boxShadow: 'inset 2px 2px 4px rgba(186,199,216,0.45), inset -2px -2px 4px rgba(255,255,255,0.85)', fontSize: 14 }}
                    >
                      <option value="">Select protocol...</option>
                      {(Array.isArray(protocols) ? protocols : []).map((p) => (
                        <option key={p.id} value={p.id}>{p.protocol_name} ({p.cell_type})</option>
                      ))}
                    </select>
                  </div>
                  <div style={{ flex: 1, minWidth: 200 }}>
                    <label style={{ fontSize: 12, color: COLORS.textLight, display: 'block', marginBottom: 4 }}>Protocol B</label>
                    <select
                      value={selectedProtocolB}
                      onChange={(e) => setSelectedProtocolB(e.target.value)}
                      style={{ width: '100%', padding: '10px 14px', borderRadius: 6, boxShadow: 'inset 2px 2px 4px rgba(186,199,216,0.45), inset -2px -2px 4px rgba(255,255,255,0.85)', fontSize: 14 }}
                    >
                      <option value="">Select protocol...</option>
                      {(Array.isArray(protocols) ? protocols : []).map((p) => (
                        <option key={p.id} value={p.id}>{p.protocol_name} ({p.cell_type})</option>
                      ))}
                    </select>
                  </div>
                  <button
                    onClick={handleComputeSimilarity}
                    disabled={!selectedProtocolA || !selectedProtocolB || selectedProtocolA === selectedProtocolB || loading}
                    style={{
                      padding: '10px 24px', borderRadius: 6, border: 'none',
                      background: selectedProtocolA && selectedProtocolB && selectedProtocolA !== selectedProtocolB ? COLORS.primary : COLORS.textLight,
                      color: COLORS.white, fontSize: 14, fontWeight: 600, cursor: 'pointer', minWidth: 140,
                    }}
                  >
                    {loading ? 'Computing...' : 'Compare'}
                  </button>
                </div>

                {similarityResult && (
                  <div style={{ background: '#F8F9FA', borderRadius: 8, padding: 16, boxShadow: '5px 5px 10px rgba(186,199,216,0.45), -5px -5px 10px rgba(255,255,255,0.85)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 12 }}>
                      <div style={{ fontSize: 36, fontWeight: 700, color: COLORS.primary }}>
                        {similarityResult.score !== null ? `${(similarityResult.score * 100).toFixed(0)}%` : 'N/A'}
                      </div>
                      <div>
                        <div style={{ fontWeight: 600, color: COLORS.dark }}>Similarity Score</div>
                        <div style={{ fontSize: 13, color: COLORS.textLight }}>{similarityResult.summary || 'AI-computed semantic similarity'}</div>
                      </div>
                    </div>
                    {similarityResult.breakdown && (
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 8 }}>
                        {Object.entries(similarityResult.breakdown).map(([key, val]) => (
                          <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <span style={{ fontSize: 12, color: COLORS.textLight, flex: 1, textTransform: 'capitalize' }}>{key.replace(/_/g, ' ')}</span>
                            <div style={{ width: 80, height: 8, background: '#e2e8f0', borderRadius: 4, overflow: 'hidden' }}>
                              <div style={{ width: `${(val || 0) * 100}%`, height: '100%', background: COLORS.primary, borderRadius: 4 }} />
                            </div>
                            <span style={{ fontSize: 12, fontWeight: 600, color: COLORS.dark, minWidth: 35 }}>{val !== null ? `${(val * 100).toFixed(0)}%` : '—'}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* AI Recommendations */}
              <div style={{ background: COLORS.light, borderRadius: 12, padding: 20 }}>
                <h4 style={{ margin: '0 0 16px', color: COLORS.dark }}>AI Treatment Recommendations</h4>
                <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                  <div style={{ flex: 1, minWidth: 200 }}>
                    <label style={{ fontSize: 12, color: COLORS.textLight, display: 'block', marginBottom: 4 }}>Protocol (optional)</label>
                    <select
                      value={selectedRecProtocol}
                      onChange={(e) => setSelectedRecProtocol(e.target.value)}
                      style={{ width: '100%', padding: '10px 14px', borderRadius: 6, boxShadow: 'inset 2px 2px 4px rgba(186,199,216,0.45), inset -2px -2px 4px rgba(255,255,255,0.85)', fontSize: 14 }}
                    >
                      <option value="">All protocols</option>
                      {(Array.isArray(protocols) ? protocols : []).map((p) => (
                        <option key={p.id} value={p.id}>{p.protocol_name}</option>
                      ))}
                    </select>
                  </div>
                  <button
                    onClick={handleGenerateRecommendations}
                    disabled={recsLoading}
                    style={{
                      padding: '10px 24px', borderRadius: 6, border: 'none',
                      background: recsLoading ? COLORS.textLight : COLORS.green,
                      color: COLORS.white, fontSize: 14, fontWeight: 600, cursor: 'pointer',
                    }}
                  >
                    {recsLoading ? 'Generating...' : 'Generate Recommendations'}
                  </button>
                </div>

                {recommendations.length > 0 && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {recommendations.map((rec, idx) => {
                      const priorityColors = {
                        critical: { bg: '#fef2f2', border: '#DC3545', text: '#DC3545' },
                        high: { bg: '#fff7ed', border: '#F26522', text: '#F26522' },
                        medium: { bg: '#fefce8', border: '#b45309', text: '#b45309' },
                        low: { bg: '#f0fdf4', border: '#62BB46', text: '#62BB46' },
                      };
                      const pc = priorityColors[rec.priority] || priorityColors.medium;
                      return (
                        <div key={idx} style={{ background: '#F8F9FA', borderRadius: 8, padding: 16, border: `1px solid ${pc.border}`, borderLeft: `4px solid ${pc.border}` }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                            <div>
                              <span style={{ fontSize: 15, fontWeight: 600, color: COLORS.dark }}>{rec.title}</span>
                              <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                                <Badge label={rec.type?.replace(/_/g, ' ') || 'recommendation'} bgColor="#e0f2fe" textColor="#0369a1" />
                                <Badge label={rec.priority || 'medium'} bgColor={pc.bg} textColor={pc.text} />
                                {rec.confidence && <Badge label={`${(rec.confidence * 100).toFixed(0)}% confidence`} bgColor="#f5f3ff" textColor="#6d28d9" />}
                              </div>
                            </div>
                          </div>
                          <p style={{ fontSize: 13, color: COLORS.text, margin: '8px 0', lineHeight: 1.6 }}>{rec.description}</p>
                          {rec.evidence && rec.evidence.length > 0 && (
                            <div style={{ fontSize: 12, color: COLORS.textLight, marginTop: 8 }}>
                              <strong>Evidence:</strong> {rec.evidence.join(' | ')}
                            </div>
                          )}
                          {rec.impact && (
                            <div style={{ fontSize: 12, color: COLORS.textLight, marginTop: 4 }}>
                              <strong>Expected Impact:</strong> {rec.impact}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Documents Tab */}
          {activeTab === 'documents' && (
            <div>
              {/* Hidden HTML5 file input for web mode uploads */}
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleWebFileSelected}
                accept=".pdf,.docx,.doc,.txt,.csv,.xlsx,.xls,.rtf"
                multiple
                style={{ display: 'none' }}
              />

              {/* Header */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <div>
                  <h3 style={{ margin: '0 0 4px', color: COLORS.dark, fontSize: 18 }}>Document Upload &amp; AI Analysis</h3>
                  <p style={{ color: COLORS.textLight, margin: 0, fontSize: 13 }}>
                    Upload documents from your computer for AI-powered analysis. Select or drag multiple files at once.
                  </p>
                </div>
                {!isWebMode && (
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button
                      onClick={handleBrowseFiles}
                      disabled={docUploading}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 8, padding: '10px 20px',
                        background: '#F8F9FA', color: COLORS.primary, border: `1px solid ${COLORS.primary}`,
                        borderRadius: 8, cursor: docUploading ? 'not-allowed' : 'pointer', fontSize: 14, fontWeight: 600,
                        opacity: docUploading ? 0.6 : 1,
                      }}
                    >
                      <Files size={16} /> Browse Files
                    </button>
                    <button
                      onClick={handleBrowseFolder}
                      disabled={docUploading}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 8, padding: '10px 20px',
                        background: '#F8F9FA', color: COLORS.primary, border: `1px solid ${COLORS.primary}`,
                        borderRadius: 8, cursor: docUploading ? 'not-allowed' : 'pointer', fontSize: 14, fontWeight: 600,
                        opacity: docUploading ? 0.6 : 1,
                      }}
                    >
                      <FolderOpen size={16} /> Scan Folder
                    </button>
                  </div>
                )}
              </div>

              {/* Drag & Drop Zone */}
              <div
                ref={dropZoneRef}
                onDragEnter={handleDragEnter}
                onDragLeave={handleDragLeave}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                onClick={() => { if (!docUploading && fileInputRef.current) { fileInputRef.current.value = ''; fileInputRef.current.click(); } }}
                style={{
                  border: `2px dashed ${dragActive ? COLORS.primary : '#CBD5E1'}`,
                  borderRadius: 12,
                  padding: stagedFiles.length > 0 ? '20px 24px' : '32px 24px',
                  textAlign: 'center',
                  marginBottom: 20,
                  background: dragActive ? 'rgba(46,139,152,0.06)' : '#FAFBFC',
                  cursor: docUploading ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s ease',
                }}
              >
                {stagedFiles.length === 0 ? (
                  <div>
                    <Upload size={32} color={dragActive ? COLORS.primary : '#94A3B8'} style={{ marginBottom: 8 }} />
                    <p style={{ margin: '0 0 4px', fontSize: 15, fontWeight: 600, color: COLORS.dark }}>
                      {dragActive ? 'Drop files here' : 'Drag & drop files here, or click to browse'}
                    </p>
                    <p style={{ margin: 0, fontSize: 13, color: COLORS.textLight }}>
                      Select multiple files at once — PDF, Word, Excel, CSV, TXT supported
                    </p>
                  </div>
                ) : (
                  <div onClick={(e) => e.stopPropagation()}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                      <span style={{ fontSize: 14, fontWeight: 600, color: COLORS.dark }}>
                        {stagedFiles.length} file{stagedFiles.length !== 1 ? 's' : ''} ready to upload
                        <span style={{ fontWeight: 400, color: COLORS.textLight, marginLeft: 8 }}>
                          ({(stagedFiles.reduce((sum, f) => sum + f.size, 0) / 1024 / 1024).toFixed(2)} MB total)
                        </span>
                      </span>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button
                          onClick={(e) => { e.stopPropagation(); if (fileInputRef.current) { fileInputRef.current.value = ''; fileInputRef.current.click(); } }}
                          style={{
                            display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px',
                            background: '#F8F9FA', color: COLORS.primary, border: `1px solid ${COLORS.primary}`,
                            borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 600,
                          }}
                        >
                          <Plus size={14} /> Add More
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); uploadStagedFiles(); }}
                          disabled={docUploading}
                          style={{
                            display: 'flex', alignItems: 'center', gap: 6, padding: '8px 18px',
                            background: COLORS.primary, color: COLORS.white, border: 'none',
                            borderRadius: 6, cursor: docUploading ? 'not-allowed' : 'pointer', fontSize: 13, fontWeight: 600,
                            opacity: docUploading ? 0.6 : 1,
                          }}
                        >
                          <Upload size={14} /> {docUploading ? 'Uploading...' : `Upload ${stagedFiles.length} File${stagedFiles.length !== 1 ? 's' : ''}`}
                        </button>
                      </div>
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                      {stagedFiles.map((sf) => {
                        const typeColors = { pdf: '#DC3545', docx: '#1d4ed8', doc: '#1d4ed8', xlsx: '#16a34a', xls: '#16a34a', csv: '#0d9488', txt: '#6b7280', rtf: '#7c3aed' };
                        return (
                          <div key={sf.id} style={{
                            display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px 6px 12px',
                            background: COLORS.white, borderRadius: 6, fontSize: 13,
                            border: '1px solid #E2E8F0',
                          }}>
                            <File size={14} color={typeColors[sf.type] || COLORS.textLight} />
                            <span style={{ color: COLORS.dark, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{sf.name}</span>
                            <span style={{ color: COLORS.textLight, fontSize: 11 }}>({(sf.size / 1024).toFixed(0)} KB)</span>
                            <button
                              onClick={(e) => { e.stopPropagation(); removeStagedFile(sf.id); }}
                              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, lineHeight: 1, color: '#94A3B8' }}
                              title="Remove file"
                            >
                              <X size={14} />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>

              {/* KPI Cards */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 16, marginBottom: 24 }}>
                <KPICard label="Total Documents" value={docsSummary.totalDocuments} icon={FileText} color={COLORS.primary} />
                <KPICard label="AI Analyses" value={docsSummary.totalAnalyses} icon={Zap} color={COLORS.orange} />
                <KPICard label="PDF Files" value={(docsSummary.byType || []).find(t => t.file_type === 'pdf')?.count || 0} icon={File} color="#DC3545" />
                <KPICard label="Word Docs" value={(docsSummary.byType || []).find(t => t.file_type === 'docx')?.count || 0} icon={FileText} color="#1d4ed8" />
                <KPICard label="Saved Locations" value={recentLocations.length} icon={HardDrive} color={COLORS.green} />
              </div>

              {/* Path Input for Network/Manual Entry (hidden in web mode — paths reference server filesystem, not user's) */}
              {!isWebMode && <div style={{ background: COLORS.light, borderRadius: 12, padding: 20, marginBottom: 20 }}>
                <h4 style={{ margin: '0 0 12px', color: COLORS.dark, fontSize: 14 }}>
                  <HardDrive size={16} style={{ verticalAlign: 'middle', marginRight: 8 }} />
                  Upload from Path (Local Drive or Network Share)
                </h4>
                <div style={{ display: 'flex', gap: 10, marginBottom: 12 }}>
                  <input
                    type="text"
                    value={pathInput}
                    onChange={(e) => { setPathInput(e.target.value); setPathValidation(null); }}
                    placeholder="Enter path: C:\Documents\report.pdf, \\server\share\file.docx, /Volumes/LabData/study.pdf..."
                    style={{
                      flex: 1, padding: '10px 14px', borderRadius: 8, boxShadow: 'inset 2px 2px 4px rgba(186,199,216,0.45), inset -2px -2px 4px rgba(255,255,255,0.85)',
                      fontSize: 13, fontFamily: 'monospace',
                    }}
                    onKeyDown={(e) => e.key === 'Enter' && handleUploadFromPath()}
                  />
                  <button
                    onClick={handleUploadFromPath}
                    disabled={!pathInput.trim() || docUploading}
                    style={{
                      padding: '10px 20px', borderRadius: 8, border: 'none',
                      background: pathInput.trim() ? COLORS.primary : '#ccc', color: COLORS.white,
                      cursor: pathInput.trim() && !docUploading ? 'pointer' : 'not-allowed', fontSize: 13, fontWeight: 600,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    <Plus size={14} style={{ verticalAlign: 'middle', marginRight: 6 }} />
                    Add to Queue
                  </button>
                </div>

                {/* Staged paths queue */}
                {stagedPaths.length > 0 && (
                  <div style={{ marginBottom: 12 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                      <span style={{ fontSize: 13, fontWeight: 600, color: COLORS.dark }}>
                        {stagedPaths.length} file{stagedPaths.length !== 1 ? 's' : ''} queued
                        <span style={{ fontWeight: 400, color: COLORS.textLight, marginLeft: 8 }}>
                          ({(stagedPaths.reduce((s, p) => s + (p.size || 0), 0) / 1024 / 1024).toFixed(2)} MB total)
                        </span>
                      </span>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button
                          onClick={() => setStagedPaths([])}
                          style={{ padding: '6px 12px', borderRadius: 6, border: '1px solid #E2E8F0', background: COLORS.white, color: COLORS.textLight, cursor: 'pointer', fontSize: 12 }}
                        >
                          Clear All
                        </button>
                        <button
                          onClick={uploadStagedPaths}
                          disabled={docUploading}
                          style={{
                            display: 'flex', alignItems: 'center', gap: 6, padding: '6px 16px',
                            background: COLORS.primary, color: COLORS.white, border: 'none',
                            borderRadius: 6, cursor: docUploading ? 'not-allowed' : 'pointer', fontSize: 13, fontWeight: 600,
                            opacity: docUploading ? 0.6 : 1,
                          }}
                        >
                          <Upload size={14} />
                          {docUploading ? 'Uploading...' : `Upload ${stagedPaths.length} File${stagedPaths.length !== 1 ? 's' : ''}`}
                        </button>
                      </div>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      {stagedPaths.map((sp) => {
                        const typeColors = { pdf: '#DC3545', docx: '#1d4ed8', doc: '#1d4ed8', xlsx: '#16a34a', xls: '#16a34a', csv: '#0d9488', txt: '#6b7280', rtf: '#7c3aed' };
                        const ext = sp.ext?.replace('.', '') || '';
                        return (
                          <div key={sp.id} style={{
                            display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px 6px 12px',
                            background: COLORS.white, borderRadius: 6, fontSize: 13, border: '1px solid #E2E8F0',
                          }}>
                            {sp.isNetwork ? <Network size={14} color={COLORS.primary} /> : <HardDrive size={14} color={COLORS.textLight} />}
                            <File size={14} color={typeColors[ext] || COLORS.textLight} />
                            <span style={{ color: COLORS.dark, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={sp.path}>{sp.name}</span>
                            <span style={{ color: COLORS.textLight, fontSize: 11, whiteSpace: 'nowrap' }}>({(sp.size / 1024).toFixed(0)} KB)</span>
                            <button
                              onClick={() => removeStagedPath(sp.id)}
                              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, color: '#94A3B8' }}
                              title="Remove from queue"
                            >
                              <X size={14} />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {pathValidation && !pathValidation.valid && (
                  <div style={{ padding: '8px 12px', background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 6, fontSize: 13, color: '#991b1b' }}>
                    <AlertCircle size={14} style={{ verticalAlign: 'middle', marginRight: 6 }} />
                    {pathValidation.error}
                    {pathValidation.isNetwork && <span style={{ marginLeft: 8, fontSize: 12, color: '#6b7280' }}>(Network path — check connectivity)</span>}
                  </div>
                )}
                {pathValidation && pathValidation.valid && (
                  <div style={{ padding: '8px 12px', background: '#dcfce7', border: '1px solid #86efac', borderRadius: 6, fontSize: 13, color: '#166534' }}>
                    <CheckCircle size={14} style={{ verticalAlign: 'middle', marginRight: 6 }} />
                    {pathValidation.isFile
                      ? `File: ${pathValidation.fileName} (${(pathValidation.size / 1024).toFixed(1)} KB) — ${pathValidation.isSupported ? 'Supported' : 'Unsupported format'}`
                      : `Folder: ${pathValidation.documentCount} supported documents found`}
                    {pathValidation.isNetwork && (
                      <span style={{ marginLeft: 8 }}>
                        <Network size={12} style={{ verticalAlign: 'middle' }} /> {pathValidation.pathType.replace('_', ' ')}
                      </span>
                    )}
                  </div>
                )}

                {/* Recent Locations */}
                {recentLocations.length > 0 && (
                  <div style={{ marginTop: 16 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: COLORS.dark, marginBottom: 8 }}>
                      <Clock size={14} style={{ verticalAlign: 'middle', marginRight: 6 }} />
                      Recent Locations
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                      {recentLocations.slice(0, 8).map((loc) => (
                        <button
                          key={loc.id}
                          onClick={() => handleUploadFromRecent(loc.location_path)}
                          disabled={!loc.accessible}
                          title={loc.location_path}
                          style={{
                            display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px',
                            borderRadius: 6, boxShadow: '5px 5px 10px rgba(186,199,216,0.45), -5px -5px 10px rgba(255,255,255,0.85)',
                            background: loc.accessible ? COLORS.white : '#f3f4f6',
                            cursor: loc.accessible ? 'pointer' : 'not-allowed', fontSize: 12,
                            color: loc.accessible ? COLORS.text : '#9ca3af',
                            opacity: loc.accessible ? 1 : 0.6,
                          }}
                        >
                          {loc.is_network ? <Network size={12} /> : <HardDrive size={12} />}
                          {loc.location_name}
                          {!loc.accessible && <span style={{ color: '#ef4444', fontSize: 10 }}> (offline)</span>}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>}

              {/* Upload Results */}
              {docUploadResults && docUploadResults.documents && (
                <div style={{ marginBottom: 20, background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 10, padding: 16 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                    <h4 style={{ margin: 0, color: '#166534', fontSize: 14 }}>Upload Results</h4>
                    <button onClick={() => setDocUploadResults(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280' }}>
                      <X size={16} />
                    </button>
                  </div>
                  {docUploadResults.documents.map((r, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0', fontSize: 13 }}>
                      {r.success
                        ? <><CheckCircle size={14} color="#15803d" /> <span>{r.file}</span> <span style={{ color: '#6b7280' }}>— {r.wordCount} words, {r.pageCount} pages</span></>
                        : <><AlertCircle size={14} color="#DC3545" /> <span>{r.file}</span> <span style={{ color: '#DC3545' }}>— {r.message}</span></>
                      }
                    </div>
                  ))}
                </div>
              )}

              {/* Filter Bar + Document List */}
              <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
                <div style={{ flex: 1, position: 'relative' }}>
                  <Search size={16} style={{ position: 'absolute', left: 12, top: 11, color: COLORS.textLight }} />
                  <input
                    type="text" placeholder="Search documents..."
                    value={docSearchTerm} onChange={(e) => setDocSearchTerm(e.target.value)}
                    style={{ width: '100%', padding: '10px 10px 10px 36px', borderRadius: 8, boxShadow: 'inset 2px 2px 4px rgba(186,199,216,0.45), inset -2px -2px 4px rgba(255,255,255,0.85)', fontSize: 13, boxSizing: 'border-box' }}
                  />
                </div>
                <select value={docTypeFilter} onChange={(e) => setDocTypeFilter(e.target.value)}
                  style={{ padding: '10px 14px', borderRadius: 8, boxShadow: 'inset 2px 2px 4px rgba(186,199,216,0.45), inset -2px -2px 4px rgba(255,255,255,0.85)', fontSize: 13 }}>
                  <option value="All">All Types</option>
                  <option value="pdf">PDF</option>
                  <option value="docx">Word (DOCX)</option>
                </select>
                <select value={docCategoryFilter} onChange={(e) => setDocCategoryFilter(e.target.value)}
                  style={{ padding: '10px 14px', borderRadius: 8, boxShadow: 'inset 2px 2px 4px rgba(186,199,216,0.45), inset -2px -2px 4px rgba(255,255,255,0.85)', fontSize: 13 }}>
                  <option value="All">All Categories</option>
                  <option value="general">General</option>
                  <option value="research">Research</option>
                  <option value="protocol">Protocol</option>
                  <option value="regulatory">Regulatory</option>
                  <option value="safety">Safety</option>
                  <option value="clinical_trial">Clinical Trial</option>
                </select>
                <button onClick={loadDocuments} style={{ padding: '10px 14px', borderRadius: 8, boxShadow: 'inset 2px 2px 4px rgba(186,199,216,0.45), inset -2px -2px 4px rgba(255,255,255,0.85)', background: '#F8F9FA', cursor: 'pointer' }}>
                  <RefreshCw size={16} color={COLORS.textLight} />
                </button>
              </div>

              {/* Document Table */}
              <div style={{ background: '#F8F9FA', borderRadius: 12, boxShadow: '5px 5px 10px rgba(186,199,216,0.45), -5px -5px 10px rgba(255,255,255,0.85)', overflow: 'hidden', marginBottom: 20 }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ background: COLORS.light }}>
                      <th style={{ padding: '12px 14px', textAlign: 'left', fontWeight: 600, color: COLORS.dark }}>Document</th>
                      <th style={{ padding: '12px 14px', textAlign: 'left', fontWeight: 600, color: COLORS.dark }}>Type</th>
                      <th style={{ padding: '12px 14px', textAlign: 'right', fontWeight: 600, color: COLORS.dark }}>Size</th>
                      <th style={{ padding: '12px 14px', textAlign: 'right', fontWeight: 600, color: COLORS.dark }}>Words</th>
                      <th style={{ padding: '12px 14px', textAlign: 'left', fontWeight: 600, color: COLORS.dark }}>Category</th>
                      <th style={{ padding: '12px 14px', textAlign: 'center', fontWeight: 600, color: COLORS.dark }}>Analyses</th>
                      <th style={{ padding: '12px 14px', textAlign: 'left', fontWeight: 600, color: COLORS.dark }}>Uploaded</th>
                      <th style={{ padding: '12px 14px', textAlign: 'center', fontWeight: 600, color: COLORS.dark }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {documents.length === 0 ? (
                      <tr><td colSpan={8} style={{ padding: 40, textAlign: 'center', color: COLORS.textLight }}>
                        <FileText size={32} style={{ marginBottom: 8, opacity: 0.4 }} /><br />
                        No documents uploaded yet. Use the buttons above to upload PDF or Word files.
                      </td></tr>
                    ) : documents.map((doc) => (
                      <tr key={doc.id} style={{
                        borderBottom: `1px solid ${COLORS.border}`,
                        background: selectedDocument?.id === doc.id ? '#e0f2fe' : COLORS.white,
                        cursor: 'pointer',
                      }}
                        onClick={() => setSelectedDocument(doc)}
                      >
                        <td style={{ padding: '10px 14px' }}>
                          <div style={{ fontWeight: 500, color: COLORS.dark }}>{doc.file_name}</div>
                          <div style={{ fontSize: 11, color: COLORS.textLight }}>{doc.uploaded_by_name}</div>
                        </td>
                        <td style={{ padding: '10px 14px' }}>
                          <Badge
                            label={doc.file_type?.toUpperCase()}
                            bgColor={doc.file_type === 'pdf' ? '#fef2f2' : '#dbeafe'}
                            textColor={doc.file_type === 'pdf' ? '#DC3545' : '#1d4ed8'}
                          />
                        </td>
                        <td style={{ padding: '10px 14px', textAlign: 'right', color: COLORS.textLight }}>
                          {doc.file_size_bytes ? (doc.file_size_bytes / 1024).toFixed(1) + ' KB' : '—'}
                        </td>
                        <td style={{ padding: '10px 14px', textAlign: 'right' }}>{doc.word_count?.toLocaleString() || '—'}</td>
                        <td style={{ padding: '10px 14px' }}>
                          <Badge label={doc.category || 'general'} bgColor="#f1f5f9" textColor="#475569" />
                        </td>
                        <td style={{ padding: '10px 14px', textAlign: 'center' }}>
                          {doc.ai_analysis_count > 0
                            ? <Badge label={doc.ai_analysis_count} bgColor="#fef3c7" textColor="#b45309" />
                            : <span style={{ color: COLORS.textLight }}>—</span>}
                        </td>
                        <td style={{ padding: '10px 14px', color: COLORS.textLight, fontSize: 12 }}>
                          {doc.created_at ? new Date(doc.created_at).toLocaleDateString() : '—'}
                        </td>
                        <td style={{ padding: '10px 14px', textAlign: 'center' }}>
                          <div style={{ display: 'flex', gap: 4, justifyContent: 'center' }}>
                            <button title="Open" onClick={(e) => { e.stopPropagation(); handleOpenDocument(doc.id); }}
                              style={{ padding: 6, boxShadow: '5px 5px 10px rgba(186,199,216,0.45), -5px -5px 10px rgba(255,255,255,0.85)', borderRadius: 6, background: '#F8F9FA', cursor: 'pointer' }}>
                              <Eye size={14} color={COLORS.textLight} />
                            </button>
                            <button title="Analyze" onClick={(e) => { e.stopPropagation(); setSelectedDocument(doc); }}
                              style={{ padding: 6, boxShadow: '5px 5px 10px rgba(186,199,216,0.45), -5px -5px 10px rgba(255,255,255,0.85)', borderRadius: 6, background: '#F8F9FA', cursor: 'pointer' }}>
                              <Zap size={14} color={COLORS.orange} />
                            </button>
                            <button title="Re-Extract Text" onClick={(e) => { e.stopPropagation(); handleReextractDocument(doc.id); }}
                              style={{ padding: 6, boxShadow: '5px 5px 10px rgba(186,199,216,0.45), -5px -5px 10px rgba(255,255,255,0.85)', borderRadius: 6, background: '#F8F9FA', cursor: 'pointer' }}>
                              <RefreshCw size={14} color="#0891b2" />
                            </button>
                            <button title="History" onClick={(e) => { e.stopPropagation(); handleLoadDocAnalysisHistory(doc.id); }}
                              style={{ padding: 6, boxShadow: '5px 5px 10px rgba(186,199,216,0.45), -5px -5px 10px rgba(255,255,255,0.85)', borderRadius: 6, background: '#F8F9FA', cursor: 'pointer' }}>
                              <Clock size={14} color={COLORS.textLight} />
                            </button>
                            <button title="Delete" onClick={(e) => { e.stopPropagation(); handleDeactivateDocument(doc.id); }}
                              style={{ padding: 6, boxShadow: '5px 5px 10px rgba(186,199,216,0.45), -5px -5px 10px rgba(255,255,255,0.85)', borderRadius: 6, background: '#F8F9FA', cursor: 'pointer' }}>
                              <Trash2 size={14} color="#DC3545" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* AI Analysis Panel (when a document is selected) */}
              {selectedDocument && (
                <div style={{ background: COLORS.light, borderRadius: 12, padding: 20, marginBottom: 20, border: `2px solid ${COLORS.primary}` }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                    <div>
                      <h4 style={{ margin: '0 0 4px', color: COLORS.dark, fontSize: 16 }}>
                        <Zap size={18} style={{ verticalAlign: 'middle', marginRight: 8, color: COLORS.orange }} />
                        AI Analysis — {selectedDocument.file_name}
                      </h4>
                      <span style={{ fontSize: 12, color: COLORS.textLight }}>
                        {selectedDocument.word_count?.toLocaleString()} words • {selectedDocument.page_count} pages • {selectedDocument.file_type?.toUpperCase()}
                      </span>
                    </div>
                    <button onClick={() => setSelectedDocument(null)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
                      <X size={18} color={COLORS.textLight} />
                    </button>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: 12, marginBottom: 16 }}>
                    <select value={docAnalysisType} onChange={(e) => setDocAnalysisType(e.target.value)}
                      style={{ padding: '10px 14px', borderRadius: 8, boxShadow: 'inset 2px 2px 4px rgba(186,199,216,0.45), inset -2px -2px 4px rgba(255,255,255,0.85)', fontSize: 13 }}>
                      {DOC_ANALYSIS_TYPES.map(t => (
                        <option key={t.id} value={t.id}>{t.label}</option>
                      ))}
                    </select>
                    <input
                      type="text" placeholder="Optional: specific question or focus area..."
                      value={docAnalysisQuery} onChange={(e) => setDocAnalysisQuery(e.target.value)}
                      style={{ padding: '10px 14px', borderRadius: 8, boxShadow: 'inset 2px 2px 4px rgba(186,199,216,0.45), inset -2px -2px 4px rgba(255,255,255,0.85)', fontSize: 13 }}
                      onKeyDown={(e) => e.key === 'Enter' && handleDocAnalysis()}
                    />
                    <button
                      onClick={handleDocAnalysis}
                      disabled={docAnalysisLoading}
                      style={{
                        padding: '10px 24px', borderRadius: 8, border: 'none',
                        background: COLORS.primary, color: COLORS.white, cursor: docAnalysisLoading ? 'wait' : 'pointer',
                        fontSize: 14, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8,
                        opacity: docAnalysisLoading ? 0.7 : 1,
                      }}
                    >
                      {docAnalysisLoading ? <><RotateCw size={14} className="animate-spin" /> Analyzing...</> : <><MessageSquare size={14} /> Analyze</>}
                    </button>
                  </div>

                  {/* Analysis Result */}
                  {docAnalysisResult && (
                    <div style={{ background: '#F8F9FA', borderRadius: 10, padding: 20, boxShadow: '5px 5px 10px rgba(186,199,216,0.45), -5px -5px 10px rgba(255,255,255,0.85)' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
                        <Badge label={docAnalysisResult.analysisType?.replace(/_/g, ' ')} bgColor="#e0f2fe" textColor="#0369a1" />
                        {docAnalysisResult.isAI && <Badge label="AI Generated" bgColor="#dcfce7" textColor="#15803d" />}
                      </div>
                      <div style={{
                        whiteSpace: 'pre-wrap', fontSize: 13, lineHeight: 1.7, color: COLORS.text,
                        maxHeight: 500, overflowY: 'auto', padding: '0 4px',
                      }}>
                        {docAnalysisResult.response || docAnalysisResult.message}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Analysis History Modal */}
              {showDocAnalysisHistory && (
                <div style={{ background: COLORS.light, borderRadius: 12, padding: 20, marginBottom: 20 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                    <h4 style={{ margin: 0, color: COLORS.dark }}>Analysis History</h4>
                    <button onClick={() => setShowDocAnalysisHistory(false)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
                      <X size={18} color={COLORS.textLight} />
                    </button>
                  </div>
                  {docAnalysisHistory.length === 0
                    ? <p style={{ color: COLORS.textLight, fontSize: 13 }}>No analyses found for this document.</p>
                    : docAnalysisHistory.map((a) => (
                      <div key={a.id} style={{ background: '#F8F9FA', borderRadius: 8, padding: 14, marginBottom: 10, boxShadow: '5px 5px 10px rgba(186,199,216,0.45), -5px -5px 10px rgba(255,255,255,0.85)' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                            <Badge label={a.analysis_type?.replace(/_/g, ' ')} bgColor="#e0f2fe" textColor="#0369a1" />
                            <span style={{ fontSize: 12, color: COLORS.textLight }}>{a.document_name}</span>
                          </div>
                          <span style={{ fontSize: 12, color: COLORS.textLight }}>{a.created_at ? new Date(a.created_at).toLocaleString() : ''}</span>
                        </div>
                        <div style={{ fontSize: 13, color: COLORS.textLight, marginBottom: 4 }}><strong>Query:</strong> {a.query_text}</div>
                        <div style={{ fontSize: 13, color: COLORS.text, whiteSpace: 'pre-wrap', maxHeight: 200, overflowY: 'auto' }}>
                          {a.response_text?.substring(0, 500)}{a.response_text?.length > 500 ? '...' : ''}
                        </div>
                      </div>
                    ))
                  }
                </div>
              )}
            </div>
          )}

          {/* Compliance Tab */}
          {activeTab === 'compliance' && (
            <div style={{ padding: 24 }}>
              <h3 style={{ margin: '0 0 8px', color: COLORS.dark, fontSize: 18 }}>
                Regulatory Compliance
              </h3>
              <p style={{ color: COLORS.textLight, marginBottom: 20, fontSize: 14 }}>
                21 CFR Part 11 Electronic Signatures, HIPAA Audit Trail, GDPR Compliance
              </p>

              {/* Compliance KPIs */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 16, marginBottom: 24 }}>
                <KPICard label="Signed Records" value={signatures.length || 0} icon={CheckCircle} color={COLORS.green} />
                <KPICard label="HIPAA Compliant" value="Yes" icon={AlertCircle} color={COLORS.primary} />
                <KPICard label="De-Identified AI" value="Active" icon={Zap} color={COLORS.orange} />
                <KPICard label="21 CFR Part 11" value="Enabled" icon={BookOpen} color={COLORS.dark} />
              </div>

              {/* E-Signature Section */}
              <div style={{ background: COLORS.light, borderRadius: 12, padding: 20, marginBottom: 20 }}>
                <h4 style={{ margin: '0 0 16px', color: COLORS.dark }}>Electronic Signature Audit</h4>
                <p style={{ fontSize: 13, color: COLORS.textLight, marginBottom: 16 }}>
                  Per 21 CFR Part 11, all electronic signatures are linked to unique individual identifiers,
                  timestamped, and cryptographically bound to the signed record via SHA-256 hash.
                </p>
                <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
                  <button
                    onClick={() => loadSignatures('biolab_protocols')}
                    style={{ padding: '8px 16px', borderRadius: 6, boxShadow: '5px 5px 10px rgba(186,199,216,0.45), -5px -5px 10px rgba(255,255,255,0.85)', background: '#F8F9FA', cursor: 'pointer', fontSize: 13 }}
                  >
                    Load Protocol Signatures
                  </button>
                  <button
                    onClick={() => loadSignatures('biolab_published_research')}
                    style={{ padding: '8px 16px', borderRadius: 6, boxShadow: '5px 5px 10px rgba(186,199,216,0.45), -5px -5px 10px rgba(255,255,255,0.85)', background: '#F8F9FA', cursor: 'pointer', fontSize: 13 }}
                  >
                    Load Research Signatures
                  </button>
                </div>
                {signatures.length > 0 && (
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <thead>
                      <tr style={{ background: COLORS.white }}>
                        <th style={{ padding: '8px 12px', textAlign: 'left' }}>Record</th>
                        <th style={{ padding: '8px 12px', textAlign: 'left' }}>Type</th>
                        <th style={{ padding: '8px 12px', textAlign: 'left' }}>Signer</th>
                        <th style={{ padding: '8px 12px', textAlign: 'left' }}>Role</th>
                        <th style={{ padding: '8px 12px', textAlign: 'left' }}>Meaning</th>
                        <th style={{ padding: '8px 12px', textAlign: 'left' }}>Date</th>
                        <th style={{ padding: '8px 12px', textAlign: 'left' }}>Verify</th>
                      </tr>
                    </thead>
                    <tbody>
                      {signatures.map((sig) => (
                        <tr key={sig.id} style={{ borderBottom: `1px solid ${COLORS.border}` }}>
                          <td style={{ padding: '8px 12px' }}>#{sig.record_id}</td>
                          <td style={{ padding: '8px 12px' }}><Badge label={sig.signature_type} bgColor="#e0f2fe" textColor="#0369a1" /></td>
                          <td style={{ padding: '8px 12px' }}>{sig.signer_display_name}</td>
                          <td style={{ padding: '8px 12px' }}>{sig.signer_role}</td>
                          <td style={{ padding: '8px 12px', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>{sig.signature_meaning}</td>
                          <td style={{ padding: '8px 12px' }}>{sig.created_at ? new Date(sig.created_at).toLocaleDateString() : '—'}</td>
                          <td style={{ padding: '8px 12px' }}>
                            <button
                              onClick={() => handleVerifySignature(sig.id)}
                              style={{ padding: '4px 8px', borderRadius: 4, boxShadow: '5px 5px 10px rgba(186,199,216,0.45), -5px -5px 10px rgba(255,255,255,0.85)', background: '#F8F9FA', cursor: 'pointer', fontSize: 12 }}
                            >
                              Verify
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
                {signatureVerification && (
                  <div style={{
                    marginTop: 12, padding: 12, borderRadius: 8,
                    background: signatureVerification.valid ? '#dcfce7' : '#fef2f2',
                    border: `1px solid ${signatureVerification.valid ? '#62BB46' : '#DC3545'}`,
                    fontSize: 13,
                  }}>
                    <strong>{signatureVerification.valid ? 'VALID' : 'INVALID'}:</strong>{' '}
                    Signed by {signatureVerification.signer} ({signatureVerification.role}) — {signatureVerification.meaning}
                    {signatureVerification.tampered && <span style={{ color: '#DC3545', fontWeight: 600 }}> — RECORD HAS BEEN MODIFIED</span>}
                    <div style={{ fontSize: 12, color: COLORS.textLight, marginTop: 4 }}>
                      Signed at: {signatureVerification.signedAt ? new Date(signatureVerification.signedAt).toLocaleString() : '—'} | Status: {signatureVerification.reason}
                    </div>
                  </div>
                )}
              </div>

              {/* Compliance Summary */}
              <div style={{ background: COLORS.light, borderRadius: 12, padding: 20 }}>
                <h4 style={{ margin: '0 0 16px', color: COLORS.dark }}>Compliance Framework</h4>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 16 }}>
                  {[
                    { title: 'HIPAA §164.312(b)', desc: 'Full PHI access audit logging with user, resource, timestamp, and access reason', status: 'Active' },
                    { title: 'HIPAA §164.312(a)(1)', desc: 'Role-based access control (RBAC) with minimum necessary access principle', status: 'Active' },
                    { title: 'HIPAA Safe Harbor', desc: 'AI analysis engine strips all 18 HIPAA identifiers before processing', status: 'Active' },
                    { title: '21 CFR Part 11', desc: 'Electronic signatures with SHA-256 tamper detection and audit trail', status: 'Active' },
                    { title: 'GDPR Art. 25', desc: 'Data protection by design — de-identification, access controls, audit logs', status: 'Active' },
                    { title: 'GxP Data Integrity', desc: 'ALCOA+ principles — Attributable, Legible, Contemporaneous, Original, Accurate', status: 'Active' },
                  ].map((item, idx) => (
                    <div key={idx} style={{ background: '#F8F9FA', borderRadius: 8, padding: 16, boxShadow: '5px 5px 10px rgba(186,199,216,0.45), -5px -5px 10px rgba(255,255,255,0.85)' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                        <span style={{ fontWeight: 600, fontSize: 14, color: COLORS.dark }}>{item.title}</span>
                        <Badge label={item.status} bgColor="#dcfce7" textColor="#15803d" />
                      </div>
                      <p style={{ fontSize: 13, color: COLORS.textLight, margin: 0, lineHeight: 1.5 }}>{item.desc}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      <NewProtocolModal
        isOpen={isNewProtocolOpen}
        onClose={() => setIsNewProtocolOpen(false)}
        onSave={handleNewProtocol}
        isLoading={loading}
      />

      <AddComparisonModal
        isOpen={isComparisonOpen}
        onClose={() => setIsComparisonOpen(false)}
        onSave={handleAddComparison}
        isLoading={loading}
        protocolOptions={Array.isArray(protocols) ? protocols : []}
      />

      <LogTreatmentModal
        isOpen={isTreatmentOpen}
        onClose={() => setIsTreatmentOpen(false)}
        onSave={handleLogTreatment}
        isLoading={loading}
        protocolOptions={Array.isArray(protocols) ? protocols : []}
        patientOptions={[]}
      />

      <PublishedResearchModal
        isOpen={isPublishedResearchOpen}
        onClose={() => setIsPublishedResearchOpen(false)}
        onSave={handleAddPublishedResearch}
        isLoading={loading}
      />

      <ProtocolDetailModal
        isOpen={expandedProtocol !== null}
        onClose={() => setExpandedProtocol(null)}
        protocol={expandedProtocol}
      />
    </div>
  );
}
